// src/player/player-element.js
import { ReactiveElement, ReactiveState as ReactiveState3 } from "@nrk/reactive-element";
import { CoreEvent as CoreEvent3, version as playerCoreVersion } from "@nrk/player-core/types.js";
import { guessMediaTypeOfUrl, toError, unlockMediaElement } from "@nrk/player-core/utils.js";
import { DimensionsMixin } from "@nrk/element-dimensions-mixin";
import { FocusVisibleMixin } from "@nrk/element-focus-visible-mixin";
import { FocusWithinController } from "@nrk/element-focus-within-controller";

// src/utils/dom.js
function getElementOfType(element, typeAttribute = "player") {
  let target = element;
  while (target && target !== document.body) {
    if (target.getAttribute("type") === typeAttribute) {
      return target;
    }
    target = target.parentElement;
  }
  throw Error(
    `no parent <xx-${typeAttribute}> element found. Ensure that "element" is a child of a <xx-${typeAttribute}>, and that <xx-${typeAttribute}> has a type="${typeAttribute}" attribute`
  );
}
function getActionElement(element) {
  let target = element;
  while (target && target !== document.body) {
    if ((target.hasAttribute("action") || "action" in target) && target.tagName !== "form" && !target.hasAttribute("disabled")) {
      return target;
    }
    target = target.parentElement;
  }
}
function getActionFromEvent(event) {
  const actionElement = getActionElement(event.target);
  if (!actionElement) {
    return [void 0, void 0];
  }
  const action = actionElement.getAttribute("action") || actionElement.action;
  const actionEvents = actionElement.getAttribute("action:events")?.split(/,\s?/g) ?? actionElement.actionEvents;
  if (actionEvents && !actionEvents.includes(event.type)) {
    return [void 0, void 0];
  }
  return [action, actionElement.value];
}
function getKeyActionsFromKeyName(key) {
  const activeElement = document.activeElement;
  if (activeElement && key) {
    const action = activeElement.getAttribute(`keyaction:${key.toLowerCase()}`);
    if (action) {
      return [action, activeElement.value];
    }
  }
  return [void 0, void 0];
}
function getPositionFromEvent(element, event) {
  const x = "touches" in event ? event.changedTouches[0].clientX : event.clientX;
  const { left, width } = element.getBoundingClientRect();
  const position = (x - left) / width;
  return Math.min(1, Math.max(0, position));
}
function getFontSize(element) {
  const style = getComputedStyle(element);
  if (style.fontSize && /\d+(\.\d+)?px/.test(style.fontSize)) {
    return parseFloat(style.fontSize);
  }
  return 0;
}

// src/player/player-element.js
import iso86012 from "iso8601-duration";

// src/utils/element-panic-mixin.js
var ElementPanic = "ElementPanic";
function ElementPanicMixin(Base) {
  return class extends Base {
    panic(error) {
      this.dispatchEvent(
        new CustomEvent(ElementPanic, {
          bubbles: true,
          cancelable: true,
          detail: error
        })
      );
    }
  };
}

// src/utils/logging.js
import { getLogger as getCoreLogger } from "@nrk/player-core/utils.js";
function getLogger(namespace) {
  return getCoreLogger(namespace);
}

// src/utils/element-panic-handler-mixin.js
function PlayerPanicHandlerMixin(Base) {
  return class extends Base {
    /** The Player-Core logger instance */
    panicLogger = getLogger("PlayerPanic");
    connectedCallback() {
      super.connectedCallback && super.connectedCallback();
      this.addEventListener(ElementPanic, this.onPanic);
    }
    onPanic(event) {
      const error = event.detail;
      event.preventDefault();
      this.panicLogger.error(error);
      this.handlePanic(error);
    }
    /**
     * Super method for handling errors dispatched by player or children
     *
     * Does nothing
     */
    handlePanic(_error) {
    }
    disconnectedCallback() {
      this.removeEventListener(ElementPanic, this.onPanic);
      super.disconnectedCallback && super.disconnectedCallback();
    }
  };
}

// src/utils/platform.js
var { userAgent: ua, vendor } = window.navigator;
var isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
var isWindowsPhone = /Windows Phone/.test(ua);
var isAndroid = /android/i.test(ua);
var supportsPointerEvents = "PointerEvent" in window;
var supportsTouchEvents = "ontouchstart" in window;
var supportsPassiveEvents = false;
try {
  const options = Object.defineProperty({}, "passive", {
    get: () => {
      supportsPassiveEvents = true;
      return true;
    }
  });
  window.addEventListener("testPassive", null, options);
  window.removeEventListener("testPassive", null, options);
} catch (err) {
}
var platform_default = {
  isAndroid,
  isApple: /Apple/.exec(vendor),
  isEdge: /Edge\/1[2-9]\./.test(ua),
  isIE: /Trident/.test(ua),
  isIOS,
  isIPhone: /iPhone|iPod/.test(ua) && !window.MSStream,
  isSafari: /^((?!chrome|android).)*safari/i.test(ua),
  isLinux: /^(?!.*android).*linux.*$/i.test(ua),
  isMobile: isIOS || isWindowsPhone || isAndroid,
  isNrknoApp: /^NRK NO/.test(window.navigator.userAgent) || /[&?]viewContext=/.test(window.location && window.location.search),
  isWindowsDesktop: /Windows NT/.test(ua),
  isWindowsPhone,
  pointerEventTypes: supportsPointerEvents ? {
    enter: "pointerenter",
    down: "pointerdown",
    leave: "pointerleave",
    move: "pointermove",
    over: "pointerover",
    out: "pointerout",
    up: "pointerup"
  } : {
    enter: "mouseenter",
    down: supportsTouchEvents ? "touchstart" : "mousedown",
    leave: "mouseleave",
    move: supportsTouchEvents ? "touchmove" : "mousemove",
    over: "mouseover",
    out: "mouseout",
    up: supportsTouchEvents ? "touchend" : "mouseup"
  },
  supportsPointerEvents,
  supportsPassiveEvents
};

// src/player/player-actions.js
var PlayerActions = /* @__PURE__ */ ((PlayerActions2) => {
  PlayerActions2["EnterFullscreen"] = "player.enterFullscreen";
  PlayerActions2["ExitFullscreen"] = "player.exitFullscreen";
  PlayerActions2["EnterPip"] = "player.enterPip";
  PlayerActions2["ExitPip"] = "player.exitPip";
  PlayerActions2["EnterRemotePlayback"] = "player.enterRemotePlayback";
  PlayerActions2["EnterTheaterMode"] = "player.enterTheaterMode";
  PlayerActions2["ExitTheaterMode"] = "player.exitTheaterMode";
  PlayerActions2["Play"] = "player.play";
  PlayerActions2["Pause"] = "player.pause";
  PlayerActions2["TogglePlay"] = "player.togglePlay";
  PlayerActions2["ToggleSubtitles"] = "player.toggleSubtitles";
  PlayerActions2["Mute"] = "player.mute";
  PlayerActions2["Unmute"] = "player.unmute";
  PlayerActions2["ToggleMute"] = "player.toggleMute";
  PlayerActions2["SetPoster"] = "player.setPoster";
  PlayerActions2["SetVolume"] = "player.setVolume";
  PlayerActions2["SeekTo"] = "player.seekTo";
  PlayerActions2["SeekToLive"] = "player.seekToLive";
  PlayerActions2["SeekToRelativeTime"] = "player.seekToRelativeTime";
  PlayerActions2["SeekToPosition"] = "player.seekToPosition";
  PlayerActions2["Speak"] = "player.speak";
  PlayerActions2["StopPlayNextCountdown"] = "player.stopPlayNextCountdown";
  PlayerActions2["PlayNextEpisode"] = "player.playNextEpisode";
  PlayerActions2["SelectStream"] = "player.selectStream";
  PlayerActions2["SkipToPoint"] = "player.skipToPoint";
  PlayerActions2["ShowOverlayMenu"] = "player.showOverlayMenu";
  PlayerActions2["HideOverlayMenu"] = "player.hideOverlayMenu";
  PlayerActions2["ShowSettings"] = "player.showSettings";
  PlayerActions2["ShowChapters"] = "player.showChapters";
  PlayerActions2["SelectSubtitles"] = "player.selectSubtitles";
  return PlayerActions2;
})(PlayerActions || {});

// src/player/playback-state-mixin.js
import { CoreEvent as CoreEvent2, PausedReason, WaitingReason } from "@nrk/player-core/types.js";

// src/player/player-events.js
import { CoreEvent as PlayerCoreEvents } from "@nrk/player-core/types.js";
var PlayerEvents = {
  ...PlayerCoreEvents,
  EPGChange: "player.EPGChange",
  IndexpointsUpdated: "player.IndexpointsUpdated",
  PlayerError: "player.error",
  PlayNextEpisode: "player.playNextEpisode",
  TrackProgress: "player.trackProgress",
  TrackQoS: "player.trackQoS",
  EcommerceConsumeStart: "player.ecommerceConsumeStart",
  EcommerceConsumeComplete: "player.ecommerceConsumeComplete",
  PlaybackFinished: "player.playbackFinished",
  EnterTheaterMode: "player.enterTheaterMode",
  ExitTheaterMode: "player.exitTheaterMode",
  StreamChange: "player.streamChange",
  ShowChapters: "player.showChapters",
  ShowSettings: "player.showSettings"
};
function isPlayerEvent(event, eventType) {
  return isTypedCustomEventDetail(event, eventType);
}
function isTypedCustomEventDetail(event, eventType) {
  return event.type === eventType;
}

// src/player/subtitles-state.js
import { CoreEvent } from "@nrk/player-core/types.js";
import { ReactiveState } from "@nrk/reactive-element";
var _logger = getLogger("SubtitleState");
var SubtitleState = class extends ReactiveState {
  /** Flag for subtitle visibility */
  isShowing = false;
  /** The currently active track id */
  active;
  /** The currently active track label */
  activeLabel;
  /** The currently visible subtitle cues */
  activeCues = [];
  /** The subtitle tracks available for the stream */
  subtitles = [];
  _session;
  constructor(session, manifest, preferredSubtitle) {
    super();
    this._session = session;
    this._init(manifest, preferredSubtitle);
    session.on(CoreEvent.SubtitlesCueUpdate, this._onSubtitleCueUpdate, this);
    session.on(CoreEvent.SubtitlesChange, this._onSubtitleChange, this);
  }
  /**
   * Toggle subtitle visibility
   * @todo - add support for choosing track
   */
  toggleVisibility() {
    if (!this._session) {
      return;
    }
    if (this.isShowing) {
      this.isShowing = false;
      this._session.setNoSubtitles();
    } else {
      this.isShowing = true;
      this._session.setAutoSubtitles();
    }
  }
  destroy() {
    super.destroy();
    this._session = void 0;
  }
  async _init(manifest, preferredSubtitle) {
    let subtitles = this._session.getAvailableSubtitles();
    if (manifest) {
      if (subtitles?.length === 0) {
        subtitles = await addExternalSubtitles(this._session, manifest);
      }
      if (!this._session) {
        return;
      }
      if (preferredSubtitle) {
        const preference = subtitles.find((s) => s.label === preferredSubtitle);
        if (preference) {
          this._session.setActiveSubtitles(preference.id, { byUser: false });
        } else if (preferredSubtitle === "none") {
          this._session.setNoSubtitles({ byUser: false });
        }
      } else {
        this._session.setDefaultSubtitles();
        if (this._session.getActiveSubtitles() === void 0) {
          const defaultSubtitleId = findDefaultSubtitleId(subtitles, manifest);
          if (defaultSubtitleId) {
            this._session.setActiveSubtitles(defaultSubtitleId, { byUser: false });
          }
        }
      }
    }
    this.isShowing = this._session.getActiveSubtitles() !== void 0;
    this._session.disableNativeSubtitlesDisplay();
    this.subtitles = subtitles;
  }
  _onSubtitleCueUpdate(event) {
    this.activeCues = event.activeCues;
  }
  _onSubtitleChange(event) {
    this.active = event.active ? event.active.id : void 0;
    this.activeLabel = event.active ? event.active.label : void 0;
    this.isShowing = event.active ? true : false;
  }
};
async function addExternalSubtitles(session, manifest) {
  const subtitles = [];
  for (const subtitle of manifest.playable.subtitles) {
    try {
      const variant = normalizeSubtitleType(subtitle.type);
      const addedSubtitle = await session.addSubtitlesTrack(
        subtitle.webVtt,
        subtitle.label,
        subtitle.language,
        variant,
        {
          defaultOn: subtitle.defaultOn
        }
      );
      if (addedSubtitle) {
        subtitles.push(addedSubtitle);
      }
    } catch (error) {
      _logger.error(error);
    }
  }
  return subtitles;
}
function normalizeSubtitleType(type) {
  const mapping = {
    nor: void 0,
    gbp: void 0,
    ttv: "public.accessibility.describes-spoken-dialog",
    mix: "public.accessibility.describes-spoken-dialog,public.accessibility.describes-music-and-sound"
  };
  return type in mapping ? mapping[type] : type;
}
function findDefaultSubtitleId(subtitles, manifest) {
  const defaultManifestSubtitle = manifest.playable.subtitles.find((sub) => sub.defaultOn);
  if (!defaultManifestSubtitle) {
    return void 0;
  }
  if (subtitles.length === 1) {
    return subtitles[0].id;
  }
  const defaultSubtitle = subtitles.find(
    (sub) => sub.label === defaultManifestSubtitle.label || sub.label === defaultManifestSubtitle.type
  );
  return defaultSubtitle?.id;
}

// src/player/playback-state-mixin.js
import { ReactiveState as ReactiveState2 } from "@nrk/reactive-element";
var SEEKABLE_STEP_PERCENT = 0.02;
function PlaybackStateMixin(Base) {
  return class extends Base {
    static get reactiveAttributes() {
      return {
        ...super.reactiveAttributes,
        muted: Boolean
      };
    }
    static get reactiveProperties() {
      return {
        ...super.reactiveProperties,
        activeSession: Object,
        currentLiveProgramTitle: String,
        fullscreenState: Object,
        isCurrentTimeLive: Boolean,
        isAudioOnly: Boolean,
        isEnded: Boolean,
        isLive: Boolean,
        isPlaying: Boolean,
        isRealtime: Boolean,
        isSeeking: Boolean,
        isStarted: Boolean,
        isTimeRunning: Boolean,
        isWaiting: Boolean,
        manifest: Object,
        pipState: Object,
        playbackRate: Number,
        remotePlaybackState: Object,
        seekableStep: Number,
        subtitleState: ReactiveState2,
        volume: Number
      };
    }
    /** The Player-Core PlaybackSession instance for current source */
    activeSession;
    currentLiveProgramTitle;
    /** The current Fullscreen state reported by Player-Core */
    fullscreenState;
    /** Is the "video" only audio with a single image as video */
    isAudioOnly = false;
    /** Are we watching the stream live */
    isCurrentTimeLive = true;
    /** Max time in seconds currentTime (playhead) can be from `endTime` and still be considered as live (not reactive)*/
    isCurrentTimeLiveCutOffTime = 15;
    /** Flag indicating that media playback has completed */
    isEnded = false;
    /** Flag indicating that media playback is a channel or event */
    isLive = false;
    /** Flag indicating that media playback has started */
    isPlaying = false;
    /**
     * Flag indicating that a stream is broadcasting in real time.
     * During a live event, this will change to "false" when the event has completed.
     */
    isRealtime = false;
    /** Flag indicating that we are seeking for new media playback position */
    isSeeking = false;
    /** Flag indicating that media playback has started */
    isStarted = false;
    /** Flag indicating that media playback is ongoing */
    isTimeRunning = false;
    /** Flag indicating that media playback has temporarily stopped pending more data */
    isWaiting = false;
    manifest;
    /** Flag indicating that audio is muted */
    muted = false;
    /** The current Picture-In-Picture state reported by Player-Core */
    pipState;
    /** Playback speed where 1.0 is "normal" */
    playbackRate = 1;
    /** The current Remote-Playback (airplay) state reported by Player-Core */
    remotePlaybackState;
    /** The seekable step size in seconds derived from the current MediaRange */
    seekableStep = 1;
    /** All things regarding subtitles */
    subtitleState;
    /** The audio volume level from 0 to 1 */
    volume = 1;
    preferredSubtitle;
    updating(propName, oldValue) {
      super.updating(propName, oldValue);
      const isCurrentTimeLiveDependencies = ["isPlaying", "isRealtime", "isSeeking", "isWaiting"];
      if (isCurrentTimeLiveDependencies.includes(propName)) {
        this._updateCurrentTimeIsLive();
      }
    }
    _observePlaybackSession(session) {
      this.activeSession = session;
      this.fullscreenState = session.getFullscreenState();
      this.pipState = session.getPiPState();
      this.remotePlaybackState = session.getRemotePlaybackState();
      this.isPlaying = session.isPlaying();
      this.isWaiting = session.isWaiting();
      session.on(CoreEvent2.Broken, this._onBroken, this);
      session.on(CoreEvent2.FullscreenStateChange, this._onFullscreenStateChange, this);
      session.on(CoreEvent2.LiveStreamEnded, this._onLiveStreamEnded, this);
      session.on(CoreEvent2.MediaInfoLoaded, this._onMediaInfoLoaded, this);
      session.on(CoreEvent2.MediaRangeChange, this._onMediaRangeChange, this);
      session.on(CoreEvent2.MutedChange, this._onMutedChange, this);
      session.on(CoreEvent2.PiPStateChange, this._onPipStateChange, this);
      session.on(CoreEvent2.PlayingChange, this._onPlayingChange, this);
      session.on(CoreEvent2.RateChange, this._onRateChange, this);
      session.on(CoreEvent2.RemotePlaybackStateChange, this._onRemotePlaybackStateChange, this);
      session.once(CoreEvent2.ScrubberUpdate, this._onScrubberUpdate, this);
      session.on(CoreEvent2.TimeRunningChange, this._onTimeRunningChange, this);
      session.on(CoreEvent2.VolumeChange, this._onVolumeChange, this);
      session.on(CoreEvent2.WaitingChange, this._onWaitingChange, this);
      this.addEventListener(PlayerEvents.EPGChange, this._onEPGChange);
    }
    _unobservePlaybackSession(session) {
      if (session) {
        this.activeSession = void 0;
        this.isAudioOnly = false;
        this.isCurrentTimeLive = false;
        this.isEnded = false;
        this.isLive = false;
        this.isPlaying = false;
        this.isRealtime = false;
        this.isSeeking = false;
        this.isStarted = false;
        this.isTimeRunning = false;
        this.isWaiting = false;
        this.seekableStep = 1;
        this.subtitleState?.destroy();
        this.subtitleState = void 0;
        this.removeEventListener(PlayerEvents.EPGChange, this._onEPGChange);
        window.removeEventListener("beforeunload", this._onWindowUnload);
      }
    }
    _onEPGChange(event) {
      const customEvent = event;
      this.currentLiveProgramTitle = customEvent.detail ? customEvent.detail.title : void 0;
    }
    _onBroken(event) {
      if ("panic" in this && typeof this.panic === "function") {
        this.panic(event.error);
      } else {
        this.error = event.error;
      }
    }
    _onFullscreenStateChange(event) {
      this.fullscreenState = event.state;
      if (this.fullscreenState.isActive) {
        window.addEventListener("beforeunload", this._onWindowUnload);
      } else {
        window.removeEventListener("beforeunload", this._onWindowUnload);
      }
    }
    _onLiveStreamEnded() {
      this.isRealtime = false;
    }
    _onMediaInfoLoaded(event) {
      this.isAudioOnly = event.isAudioOnly ?? this.isAudioOnly;
      this.isLive = this.activeSession?.isLiveStream() ?? false;
      this.isRealtime = this.isLive && !this.activeSession?.isLiveStreamEnded();
      if (this.activeSession) {
        this.subtitleState = new SubtitleState(this.activeSession, this.manifest, this.preferredSubtitle);
      }
    }
    _onScrubberUpdate() {
      this.isStarted = true;
    }
    _onMediaRangeChange(event) {
      this.seekableStep = Math.round(Math.max(1, (event.end - event.start) * SEEKABLE_STEP_PERCENT));
    }
    _onMutedChange(muted) {
      this.muted = muted;
    }
    _onPipStateChange(event) {
      this.pipState = event.state;
    }
    _onPlayingChange(event) {
      this.isPlaying = event.isPlaying;
      this.isEnded = event.pausedReason === PausedReason.Ended;
      if (this.isEnded) {
        this.isStarted = false;
      }
    }
    _onRateChange(rate) {
      this.playbackRate = rate;
    }
    _onRemotePlaybackStateChange(event) {
      this.remotePlaybackState = event.state;
    }
    _onTimeRunningChange(event) {
      this.isTimeRunning = event.isTimeRunning;
      this.isEnded = false;
    }
    _onVolumeChange(event) {
      this.volume = event;
    }
    _onWaitingChange(event) {
      if (event.reason === WaitingReason.Seeking) {
        this.isSeeking = event.isWaiting;
      }
      this.isWaiting = event.isWaiting;
    }
    _onWindowUnload(_event) {
      document.exitFullscreen?.().catch((_err) => {
      });
    }
    _updateCurrentTimeIsLive() {
      if (!this.activeSession) {
        return;
      }
      if (!this.isRealtime || !this.isPlaying) {
        this.isCurrentTimeLive = false;
        return;
      }
      const scrubberEnd = this.activeSession.getScrubberRange().end;
      const scrubberEndOnePercent = Math.max(scrubberEnd / 100, 1);
      const scrubberEndFivePercent = scrubberEndOnePercent * 5;
      const currentTime = this.activeSession.getCurrentTime();
      const cutOffTime = Math.min(this.isCurrentTimeLiveCutOffTime, scrubberEndFivePercent);
      this.isCurrentTimeLive = scrubberEnd - currentTime < cutOffTime;
    }
  };
}

// src/player/player-element.js
import { ShadowPropertiesMixin } from "@nrk/element-shadow-properties-mixin";

// src/player/player-error.js
import { PlaybackSessionErrorType } from "@nrk/player-core/types.js";
import { PSAPIRequestErrorType, NonPlayableErrorType } from "@nrk/player-psapi-client";
var PlayerSourceErrorTypes = /* @__PURE__ */ ((PlayerSourceErrorTypes2) => {
  PlayerSourceErrorTypes2["MISSING_SOURCE"] = "MISSING_SOURCE";
  PlayerSourceErrorTypes2["SOURCE_NOT_SUPPORTED"] = "SOURCE_NOT_SUPPORTED";
  return PlayerSourceErrorTypes2;
})(PlayerSourceErrorTypes || {});
var PlayerErrorTypes = {
  ...PlaybackSessionErrorType,
  ...PSAPIRequestErrorType,
  ...NonPlayableErrorType,
  ...PlayerSourceErrorTypes
};
var PlayerError = class _PlayerError extends Error {
  type;
  cause;
  fatal;
  sessionInfo;
  constructor(type, fatal, cause, sessionInfo) {
    super(cause.message);
    Object.setPrototypeOf(this, _PlayerError.prototype);
    this.type = type;
    this.fatal = fatal;
    this.cause = cause;
    this.name = "PlayerError";
    this.sessionInfo = sessionInfo;
  }
};

// src/utils/duration.js
import iso8601 from "iso8601-duration";

// src/utils/date.js
var WEEK_DAYS = ["S\xF8ndag", "Mandag", "Tirsdag", "Onsdag", "Torsdag", "Fredag", "L\xF8rdag"];
function datesAreSameDay(date1, date2) {
  return date1.getFullYear() === date2.getFullYear() && date1.getMonth() === date2.getMonth() && date1.getDate() === date2.getDate();
}

// src/utils/duration.js
var TIMEUNITS_NO = {
  hour: ["time", "r"],
  minute: ["minutt", "er"],
  second: ["sekund", "er"]
};
function parseDuration(duration) {
  if (isDuration(duration)) {
    return duration;
  } else if (duration instanceof Date) {
    return {
      hours: duration.getHours(),
      minutes: duration.getMinutes(),
      seconds: duration.getSeconds()
    };
  } else if (typeof duration === "number") {
    duration = Math.abs(duration);
    return {
      hours: Math.floor(duration / 3600),
      minutes: Math.floor(duration / 60 % 60),
      seconds: Math.floor(duration % 60)
    };
  } else if (typeof duration === "string") {
    return iso8601.parse(duration);
  } else {
    return {
      hours: 0,
      minutes: 0,
      seconds: 0
    };
  }
}
function formatDuration(duration) {
  if (!isDuration(duration)) {
    duration = parseDuration(duration);
  }
  const { hours = 0, minutes = 0, seconds = 0 } = duration;
  const mmss = `${pad(minutes)}:${pad(seconds)}`;
  return hours > 0 ? `${pad(hours)}:${mmss}` : mmss;
}
function durationToSeconds(duration) {
  if (!isDuration(duration)) {
    duration = parseDuration(duration);
  }
  return iso8601.toSeconds(duration);
}
function durationToReadableString(duration) {
  duration = parseDuration(duration);
  const { hours = 0, minutes = 0, seconds = 0 } = duration;
  if (!hours && !minutes && !seconds) {
    return "0 sekunder";
  }
  const hoursText = timeUnitToReadableString("hour", hours);
  const minutesText = timeUnitToReadableString("minute", minutes);
  const secondsText = !hours && timeUnitToReadableString("second", seconds);
  return [hoursText, minutesText, secondsText].filter(Boolean).join(" og ");
}
function dateToReadableString(date) {
  if (typeof date.getHours !== "function") {
    return "";
  }
  return `${date.getDate() < 10 ? `0${date.getDate()}` : date.getDate()}.${date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1}.${date.getFullYear()}, ${dateToReadableTimeString(date)}`;
}
function dateToReadableDateString(date) {
  if (typeof date.getHours !== "function") {
    return "";
  }
  return `${date.getDate() < 10 ? `0${date.getDate()}` : date.getDate()}.${date.getMonth() + 1 < 10 ? `0${date.getMonth() + 1}` : date.getMonth() + 1}.${date.getFullYear()}`;
}
function dateToReadableShortString(date1, date2) {
  const dateIsToday = datesAreSameDay(date1, date2);
  if (dateIsToday) {
    return `${dateToReadableTimeString(date1)}`;
  }
  const daysBetween = Math.abs(date2.getTime() - date1.getTime()) / 1e3 / 60 / 60 / 24;
  if (daysBetween < 5) {
    return WEEK_DAYS[date1.getDay()] + ` ${dateToReadableTimeString(date1)}`;
  }
  return dateToReadableString(date1);
}
function dateToReadableTimeString(date) {
  if (typeof date.getHours !== "function") {
    return "";
  }
  const hours = date.getHours() < 10 ? `0${date.getHours()}` : date.getHours();
  const minutes = date.getMinutes() < 10 ? `0${date.getMinutes()}` : date.getMinutes();
  return `${hours}:${minutes}`;
}
function timeUnitToReadableString(unit, time) {
  if (time === 0) {
    return;
  }
  const [noun, plural] = TIMEUNITS_NO[unit];
  return `${time} ${noun}${time !== 1 ? plural : ""}`;
}
function isDuration(duration) {
  return duration != null && typeof duration === "object" && ("hours" in duration || "minutes" in duration || "seconds" in duration);
}
function pad(num) {
  return num < 10 ? `0${num}` : String(num);
}

// src/player/player-element.js
import { PSAPIClient } from "@nrk/player-psapi-client";

// src/utils/performance.js
function addPerformanceMark(label) {
  window.performance.mark(label);
}

// src/utils/srcset.js
function generateSrcSet(webImages) {
  if (!webImages.length)
    return "";
  if (isWebImage(webImages)) {
    return webImages.map(({ uri, width }) => {
      return `${uri} ${width}w`;
    }).join(", ");
  } else {
    return webImages.map(({ url, pixelWidth }) => {
      return `${url} ${pixelWidth}w`;
    }).join(", ");
  }
}
function isWebImage(images) {
  return images[0].uri !== void 0;
}
function matchSrcset({
  srcsetConditions,
  elementWidth,
  devicePixelRatio = 1
}) {
  if (!srcsetConditions || srcsetConditions.length === 0) {
    return;
  }
  for (const { type, url, value } of srcsetConditions) {
    let diff;
    if (type === "x") {
      diff = elementWidth * devicePixelRatio - elementWidth * value;
    } else {
      diff = elementWidth - value;
    }
    if (diff <= 0) {
      return url;
    }
  }
  return srcsetConditions[srcsetConditions.length - 1].url;
}
function parseSrcset(srcset) {
  const srcsetConditions = [];
  for (const src of srcset.trim().split(/\s*,\s*/g)) {
    const [url, condition = "0w"] = src.split(" ");
    const type = condition.slice(-1);
    const value = Number(condition.slice(0, -1));
    srcsetConditions.push({ type, url, value });
  }
  return srcsetConditions.sort((a, b) => a.value - b.value);
}

// src/player/player-element.js
var FONT_SIZE_BREAK_POINT = 500;
var PLAYER_ELEMENTS_VERSION = "9.4.0";
var PlayerElement = class extends DimensionsMixin(
  FocusVisibleMixin(
    PlaybackStateMixin(ShadowPropertiesMixin(PlayerPanicHandlerMixin(ElementPanicMixin(ReactiveElement))))
  )
) {
  static get reactiveAttributes() {
    return {
      ...super.reactiveAttributes,
      blockedContentAge: Number,
      autoplay: Boolean,
      // list of feature flags enabled for player
      features: Array,
      loop: Boolean,
      preferredManifest: String,
      preferredSubtitle: String,
      preferredBandwidth: String,
      // muted - added by Playback-State mixin
      src: String,
      psapiOrigin: String,
      psapiRecommendationsOrigin: String,
      startTime: (element, propertyValue) => {
        if (!propertyValue || propertyValue.toString() === "undefined") {
          return;
        }
        if (isDate(propertyValue)) {
          element.startTimeLive = new Date(propertyValue);
        } else {
          element.startTimeOndemand = Number(propertyValue);
        }
      },
      cdnOverride: String,
      snowplowAppId: String,
      snowplowUrl: String,
      snowplowServiceId: String
    };
  }
  static get reactiveProperties() {
    return {
      ...super.reactiveProperties,
      // dimensions - added by Dimensions mixin
      // epg state for live buffers
      epgState: ReactiveState3,
      // fullscreenState - added by Playback-State mixin
      interactionPointState: Object,
      // isCurrentTimeLive - added by Playback-State mixin
      // isEnded - added by Playback-State mixin
      // isLive - added by Playback-State mixin
      // isPlaying - added by Playback-State mixin
      // isRealtime - added by Playback-State mixin
      // isStarted - added by Playback-State mixin
      // isTimeRunning - added by Playback-State mixin
      // isWaiting - added by Playback-State mixin
      // focusVisible - added by FocusVisible mixin
      // manifest - added by Playback-State mixin
      // seekMode - added by Playback-State mixin
      metadata: Object,
      // pipState - added by Playback-State mixin
      // playbackRate - added by Playback-State mixin
      poster: String,
      // remotePlaybackState - added by Playback-State mixin
      resolvedSrc: String,
      resolvedType: String,
      // seekableStep - added by Playback-State mixin
      // subtitleState - added by Playback-State mixin
      speak: String,
      // volume - added by Playback-State mixin
      //used by FocusWitinController
      focusWithin: Boolean,
      theaterMode: Boolean,
      subtitlesHidden: Boolean,
      startTimeOndemand: Number,
      user: Object
    };
  }
  // dimensions - added by Dimensions mixin
  // fullscreenState - added by Playback-State mixin
  interactionPointState;
  // isCurrentTimeLive - added by Playback-State mixin
  // isEnded - added by Playback-State mixin
  // isLive - added by Playback-State mixin
  // isPlaying - added by Playback-State mixin
  // isRealtime - added by Playback-State mixin
  // isSeeking - added by Playback-State mixin
  // isStarted - added by Playback-State mixin
  // isTimeRunning - added by Playback-State mixin
  // isWaiting - added by Playback-State mixin
  // focusVisible - added by FocusVisible mixin
  /** The current Metadata instance */
  metadata;
  // pipState - added by Playback-State mixin
  // playbackRate - added by Playback-State mixin
  /** The current poster source reference */
  poster = "";
  // remotePlaybackState - added by Playback-State mixin
  // delay (in seconds) first skip button display by this much to avoid clash with controls
  // instance variable to be able to override in players
  startupSkipPointDelay = 0;
  /** The media source reference resolved to a valid href */
  resolvedSrc;
  /** The media source type */
  resolvedType;
  // seekableStep - added by Playback-State mixin
  /** A message to speak via aria-live */
  speak = "";
  // subtitleState - added by Playback-State mixin
  // volume - added by Playback-State mixin
  // activeSession - added by Playback-State mixin
  /** Flag to trigger playing as soon as "src" has been set */
  autoplay = false;
  /** EPG State object */
  epgState;
  /** unleash feature flags */
  features = [];
  /** used by FocusWithinController */
  focusWithin = false;
  focusWithinController;
  /** Force usage of one cdn */
  cdnOverride;
  /** preferred manifest version to play */
  preferredManifest = void 0;
  /** preferred subtitle */
  preferredSubtitle = void 0;
  /** preferred bandwidth used in psapi calls to override bandwidth requirements */
  preferredBandwidth;
  /** snowplow app id */
  snowplowAppId;
  /** snowplow service id, needs to be set per player */
  snowplowServiceId = "n/a";
  /** snowplow platform, needs to be set per player */
  snowplowPlatform = "web";
  /** snowplow collector url, defaults to none */
  snowplowUrl = "";
  /** are we trying to start playback? */
  isAwaitingPlaybackStart = false;
  /** are we in binge mode or not */
  isBinging = false;
  /** Flag representing player suspension state */
  isSuspended = false;
  pendingMetadataResponse;
  pendingMetadataPlayable;
  /** The PSAPI client instance */
  psapiClient;
  /** Origin url for psapi. default to prod */
  psapiOrigin = "https://psapi.nrk.no";
  /** Origin url for psapi recommendations. default to prod */
  psapiRecommendationsOrigin = "https://anbefaling-psapi.nrk.no";
  /** The PlayerCore instance for this player, lazily initialised */
  player;
  /** The name, version and player family of this player, for tracking purposes */
  playerName = "";
  playerVersionNumber = "n/a";
  playerFamily = "player-core";
  /** Flag to trigger re-play once playback has ended */
  loop = false;
  // muted - added by Playback-State mixin
  /** The initial playhead position in seconds */
  startTimeOndemand;
  /** The initial playhead position for live streams, a timestamp/date */
  startTimeLive;
  /** The media source reference */
  src = "";
  /** UserState object supplied from context */
  user;
  /** Theater mode for player */
  theaterMode;
  /** are subtitles hidden or not */
  subtitlesHidden = false;
  /** Playback state when player suspended with call to "suspend()" */
  savedPlaybackState;
  _speakClearTimeoutId = 0;
  _countdownIntervalId = 0;
  /** The Player-Core logger instance */
  logger = getLogger("Player");
  constructor() {
    super();
    this.interactionPointHandler = this.interactionPointHandler.bind(this);
    this.getAuthToken = this.getAuthToken.bind(this);
    this.focusWithinController = new FocusWithinController(this, {
      onChanged: () => {
        this.focusWithin = this.focusWithinController.value;
        this.classList.toggle("focus-within", this.focusWithin);
      }
    });
    this.addEventListener(CoreEvent3.SessionCreated, this);
  }
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener("change", this);
    this.addEventListener("click", this);
    this.addEventListener("input", this);
  }
  readyCallback() {
    if (!this.src) {
      const source = getSourceFromElement(this);
      if (source) {
        const { src, type } = source;
        this.resolvedType = type;
        this.src = src;
      }
    }
    if (platform_default.isMobile) {
      this.classList.add("mobile");
    }
  }
  /**
   * Clean up when element removed from DOM
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    this.destroySession();
    this.psapiClient?.abort();
    this.psapiClient = void 0;
    this.removeEventListener("click", this);
    this.removeEventListener("input", this);
    this.removeEventListener("change", this);
    this.removeEventListener(CoreEvent3.SessionCreated, this);
  }
  updating(propName, oldValue) {
    super.updating(propName, oldValue);
    if (propName === "volume") {
      this.activeSession?.setVolume(this.volume);
    } else if (propName === "muted") {
      this.activeSession?.[this.muted ? "mute" : "unmute"]();
    } else if (propName === "activeSession") {
      this.activeSession?.setVolume(this.volume);
    } else if (propName === "metadata" && this.metadata) {
      this.isLive = this.metadata.isLive ?? false;
      if (this.preferredManifest !== void 0) {
        const preferredManifest = this.metadata?.manifests?.find((a) => a.type === this.preferredManifest);
        const defaultManifest = this.metadata?.manifests?.find((a) => a.type === "default");
        if (preferredManifest && !this.src.endsWith(preferredManifest.id)) {
          this.src = `nrk:${this.isLive ? "channel" : "program"}:${preferredManifest.id}`;
          this.pendingMetadataPlayable = void 0;
        } else if (!preferredManifest && defaultManifest) {
          this.src = `nrk:${this.isLive ? "channel" : "program"}:${defaultManifest.id}`;
          this.pendingMetadataPlayable = void 0;
        }
      }
    } else if (propName === "manifest" && this.manifest && "playable" in this.manifest) {
      const source = getSourceFromManifest(this.manifest);
      if (source) {
        this.resolvedSrc = source.src;
        this.resolvedType = source.type;
      }
    } else if (propName === "preferredManifest") {
      const isManifestReset = oldValue !== void 0;
      if (isManifestReset) {
        this.manifest = void 0;
        this.metadata = void 0;
        this.psapiClient?.abort();
        this.psapiClient = void 0;
        this.resolvedSrc = void 0;
        this.isSuspended = false;
        if (this.savedPlaybackState) {
          this.startTimeOndemand = this.savedPlaybackState.startTime;
        } else {
          this.startTimeOndemand = void 0;
        }
        this.stop();
        if (PSAPIClient.isSupported(this.src)) {
          const playbackOptions = {
            origin: this.psapiOrigin,
            playerFamily: this.playerFamily,
            enableEeaPortability: true
          };
          const recommendationsOptions = {
            origin: this.psapiRecommendationsOrigin
          };
          if (this.user?.id) {
            playbackOptions.authTokenFunction = this.getAuthToken;
            playbackOptions.contentGroup = this.user?.contentGroup;
          }
          playbackOptions.ageRestriction = this.user?.ageRestriction;
          if (this.cdnOverride) {
            playbackOptions.cdnOverride = this.cdnOverride;
          }
          if (this.preferredBandwidth) {
            playbackOptions.preferredBandwidth = this.preferredBandwidth;
          }
          this.psapiClient = new PSAPIClient(this.src, this.playerName, {
            playback: playbackOptions,
            recommendations: recommendationsOptions
          });
          this.loadMetadata();
        } else {
          this.resolvedSrc = this.src;
        }
      }
    } else if (propName === "src") {
      const isSrcReset = oldValue !== void 0;
      if (isSrcReset) {
        this.manifest = void 0;
        this.metadata = void 0;
        this.psapiClient?.abort();
        this.psapiClient = void 0;
        this.resolvedSrc = void 0;
        this.isSuspended = false;
        if (this.savedPlaybackState) {
          this.startTimeOndemand = this.savedPlaybackState.startTime;
        } else {
          this.startTimeOndemand = void 0;
        }
        this.stop();
      }
      if (this.src && this.src !== "undefined") {
        if (PSAPIClient.isSupported(this.src)) {
          const playbackOptions = {
            origin: this.psapiOrigin,
            playerFamily: this.playerFamily,
            enableEeaPortability: true
          };
          const reccomendationsOptions = {
            origin: this.psapiRecommendationsOrigin
          };
          if (this.user?.id) {
            playbackOptions.authTokenFunction = this.getAuthToken;
            playbackOptions.contentGroup = this.user?.contentGroup;
          }
          playbackOptions.ageRestriction = this.user?.ageRestriction;
          if (this.cdnOverride) {
            playbackOptions.cdnOverride = this.cdnOverride;
          }
          if (this.preferredBandwidth) {
            playbackOptions.preferredBandwidth = this.preferredBandwidth;
          }
          this.psapiClient = new PSAPIClient(this.src, this.playerName, {
            playback: playbackOptions,
            recommendations: reccomendationsOptions
          });
          this.loadMetadata();
        } else {
          this.resolvedSrc = this.src;
        }
      }
    } else if (propName === "isRealtime") {
      this.isRealtime ? this.classList.add("realtime") : this.classList.remove("realtime");
    } else if (propName === "isLive") {
      this.isLive ? this.classList.add("live") : this.classList.remove("live");
    } else if (propName === "isEnded" && this.isEnded) {
      this.startTimeOndemand = void 0;
    } else if (propName === "playbackRate") {
      this.activeSession?.setPlaybackRate(this.playbackRate);
    } else if (propName === "dimensions" && this.dimensions) {
      this.adjustFontSize();
    }
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    const isChangedSrc = changedProperties.has("src");
    const isValidSrc = this.src && this.src !== "undefined";
    const shouldSuspend = (this.activeSession !== void 0 || this.isAwaitingPlaybackStart) && changedProperties.has("hidden") && this.hidden;
    const shouldResume = changedProperties.has("hidden") && !this.hidden && !isChangedSrc && isValidSrc;
    const shouldPlay = isChangedSrc && isValidSrc && (changedProperties.get("isPlaying") === true || this.autoplay);
    if (isChangedSrc) {
      this.interactionPointState = void 0;
    }
    if (shouldSuspend) {
      this.suspend();
    } else if (shouldResume) {
      this.resume();
    } else if (shouldPlay) {
      this.play();
    }
    if (changedProperties.has("isStarted") && this.isStarted) {
      addPerformanceMark("playback-startup-time-end");
    }
  }
  /**
   * Handle events registered in "connectedCallback"
   */
  handleEvent(event) {
    if (event.defaultPrevented) {
      return;
    }
    const [action, value] = getActionFromEvent(event);
    if (!action) {
      switch (event.type) {
        case CoreEvent3.SessionCreated: {
          const session = event.detail.session;
          if (!session) {
            return;
          }
          if (this.activeSession) {
            this._unobservePlaybackSession(this.activeSession);
          }
          this._observePlaybackSession(session);
          break;
        }
      }
      return;
    }
    switch (action) {
      case PlayerActions.EnterFullscreen:
        event.preventDefault();
        this.activeSession?.enterFullscreen();
        break;
      case PlayerActions.ExitFullscreen:
        event.preventDefault();
        this.activeSession?.exitFullscreen();
        break;
      case PlayerActions.EnterPip:
        event.preventDefault();
        this.hideSubtitles(true);
        if (platform_default.isSafari) {
          this.activeSession?.enableNativeSubtitlesDisplay();
        }
        this.activeSession?.enterPiP();
        break;
      case PlayerActions.ExitPip:
        event.preventDefault();
        if (platform_default.isSafari) {
          this.activeSession?.disableNativeSubtitlesDisplay();
        }
        this.hideSubtitles(false);
        this.activeSession?.exitPiP();
        break;
      case PlayerActions.EnterTheaterMode:
        event.preventDefault();
        this.theaterMode = true;
        this.dispatchEvent(
          new CustomEvent(PlayerEvents.EnterTheaterMode, {
            bubbles: true,
            cancelable: true
          })
        );
        break;
      case PlayerActions.ExitTheaterMode:
        event.preventDefault();
        this.theaterMode = false;
        this.dispatchEvent(
          new CustomEvent(PlayerEvents.ExitTheaterMode, {
            bubbles: true,
            cancelable: true
          })
        );
        break;
      case PlayerActions.EnterRemotePlayback:
        event.preventDefault();
        this.activeSession?.enterRemotePlayback();
        break;
      case PlayerActions.Play:
        event.preventDefault();
        this.play();
        break;
      case PlayerActions.Pause:
        event.preventDefault();
        this.pause();
        break;
      case PlayerActions.TogglePlay:
        event.preventDefault();
        this.togglePlay();
        break;
      case PlayerActions.Mute:
        event.preventDefault();
        this.muted = true;
        break;
      case PlayerActions.Unmute:
        event.preventDefault();
        this.muted = false;
        break;
      case PlayerActions.StopPlayNextCountdown:
        this.interactionPointState = void 0;
        window.clearInterval(this._countdownIntervalId);
        break;
      case PlayerActions.PlayNextEpisode:
        this.abortInteractionPoint();
        this.playNext();
        break;
      case PlayerActions.ToggleSubtitles: {
        event.preventDefault();
        this.subtitleState?.toggleVisibility();
        break;
      }
      case PlayerActions.ToggleMute: {
        event.preventDefault();
        this.toggleMute();
        break;
      }
      case PlayerActions.SelectStream: {
        if (value !== this.src) {
          this.savedPlaybackState = { startTime: this.activeSession?.getCurrentTime() };
          this.src = value;
          this.dispatchEvent(
            new CustomEvent(PlayerEvents.StreamChange, {
              bubbles: true,
              cancelable: true,
              detail: { type: this.preferredManifestType }
            })
          );
        }
        break;
      }
      case PlayerActions.SetPoster: {
        event.preventDefault();
        if (value) {
          this.poster = value;
        }
        break;
      }
      case PlayerActions.SetVolume: {
        event.preventDefault();
        if (value !== void 0) {
          const parsedValue = typeof value === "string" ? parseFloat(value) : value;
          this.volume = Math.min(Math.max(parsedValue / 100, 0), 1);
          this.muted = this.volume === 0;
        }
        break;
      }
      case PlayerActions.SeekTo: {
        event.preventDefault();
        this.interactionPointState = void 0;
        if (value !== void 0) {
          this.seekTo(value);
        }
        break;
      }
      case PlayerActions.SeekToLive: {
        event.preventDefault();
        this.activeSession?.seekToLive();
        break;
      }
      case PlayerActions.SeekToRelativeTime: {
        event.preventDefault();
        if (value !== void 0) {
          this.seekToRelativeTime(value);
        }
        break;
      }
      case PlayerActions.SeekToPosition: {
        event.preventDefault();
        if (value !== void 0) {
          this.seekToPosition(value);
        }
        break;
      }
      case PlayerActions.SkipToPoint: {
        this.seekTo(value);
        break;
      }
      case PlayerActions.Speak: {
        event.preventDefault();
        if (value) {
          this.speak = value;
          clearTimeout(this._speakClearTimeoutId);
          this._speakClearTimeoutId = window.setTimeout(() => {
            this.speak = "";
          }, 2e3);
        }
        break;
      }
    }
  }
  /**
   * The current media playhead position in seconds
   */
  get currentTime() {
    return this.activeSession?.getCurrentTime() ?? 0;
  }
  hideSubtitles(hidden) {
    this.subtitlesHidden = hidden;
  }
  /**
   * Preload media before playback
   */
  preload(options) {
    return this.activeSession?.preload(options);
  }
  /**
   * Begin media playback
   */
  async play() {
    if (this.isAwaitingPlaybackStart) {
      return;
    }
    if (this.isSuspended) {
      return this.resume();
    }
    this.isWaiting = !this.player || !this.activeSession;
    if (!this.player) {
      try {
        await this.initPlayerCoreInstance();
      } catch (err) {
        this.isWaiting = false;
        this.panic(err);
        return;
      }
    }
    if (!this.activeSession) {
      this.isAwaitingPlaybackStart = true;
      this.unlockAudioVideoElement();
      if (PSAPIClient.isSupported(this.src)) {
        await this.loadMetadata();
      }
      if (!this.resolvedSrc) {
        await this.loadManifest(this.preferredManifestType);
        addPerformanceMark("playback-startup-time-start");
      }
      if (this.resolvedSrc) {
        await this.createSession();
      }
      this.isAwaitingPlaybackStart = false;
      if (!this.activeSession || this.isSuspended) {
        this.isWaiting = false;
        this.stop();
        return;
      }
    }
    try {
      await this.activeSession.play();
    } catch (err) {
      this.isWaiting = false;
      if (toError(err).name !== "AutoplayError") {
        this.panic(err);
      }
    }
  }
  /**
   * Pause media playback
   */
  pause() {
    if (this.activeSession?.isPlaying()) {
      this.activeSession.pause();
    }
  }
  /**
   * Toggle media playback state
   */
  togglePlay() {
    if (!this.activeSession || !this.activeSession.isPlaying()) {
      return this.play();
    }
    this.pause();
  }
  /**
   * Suspend media playback
   */
  async suspend(replayOnResume) {
    this.isSuspended = true;
    if (this.activeSession) {
      this.savedPlaybackState = {
        // Trigger play if re-playing ended media
        isPlaying: this.isEnded || this.isPlaying || replayOnResume,
        startTime: this.isStarted ? this.currentTime : this.startTimeOndemand
      };
    }
    return this.stop();
  }
  /**
   * Resume previously suspended playback
   */
  async resume() {
    this.isSuspended = false;
    if (this.savedPlaybackState) {
      const savedPlaybackState = this.savedPlaybackState;
      this.startTimeOndemand = savedPlaybackState.startTime;
      this.savedPlaybackState = void 0;
      await this.play();
      if (!savedPlaybackState.isPlaying) {
        this.pause();
        this.isStarted = true;
      }
    } else {
      this.play();
    }
  }
  /**
   * Stop media playback
   */
  stop() {
    this.isAwaitingPlaybackStart = false;
    this.psapiClient?.abort();
    return this.destroySession();
  }
  /**
   * Toggle media mute state
   */
  toggleMute() {
    this.muted = !this.muted;
  }
  /**
   * Reset playbackRate to default of 1.0
   */
  resetPlaybackRate() {
    this.activeSession?.resetPlaybackRate();
  }
  /**
   * Seek to absolute "time" in seconds
   */
  seekTo(time) {
    if (!this.activeSession) {
      return;
    }
    this.isSeeking = true;
    if (typeof time === "string") {
      time = parseFloat(time);
    }
    this.activeSession.seekTo(time);
  }
  /**
   * Seek to position using date
   */
  seekToDate(date) {
    if (!this.activeSession) {
      return;
    }
    this.isSeeking = true;
    this.seekTo(this.activeSession.liveTimeToPosition(date));
  }
  /**
   * Seek to "time" with relative offset
   */
  seekToRelativeTime(time) {
    if (!this.activeSession) {
      return;
    }
    this.isSeeking = true;
    if (typeof time === "string") {
      time = parseFloat(time);
    }
    this.activeSession.seekToRelativeTime(time);
  }
  /**
   * Seek to "position" from 0 (start) to 1 (end) of current seekable range
   */
  seekToPosition(position) {
    if (!this.activeSession) {
      return;
    }
    this.isSeeking = true;
    if (typeof position === "string") {
      position = parseFloat(position);
    }
    const range = this.activeSession.getSeekableRange();
    const time = position * (range.end - range.start) + range.start;
    this.activeSession.seekTo(time);
  }
  /**
   * Dispatch custom player event
   */
  emit(type, detail) {
    this.dispatchEvent(new CustomEvent(type, { bubbles: false, detail }));
  }
  interactionPointHandler(event) {
    const eventData = event.range.data;
    if (event.type === "enter") {
      if (eventData.autoskipInBingeMode && this.isBinging) {
        this.seekTo(eventData.seekToTime);
        this.isBinging = false;
        return;
      }
      this.interactionPointState = {
        type: eventData.type,
        visible: true,
        displayValue: eventData.displayValue,
        seekToTime: eventData.seekToTime
      };
      if (eventData.type === "nextUpPoint") {
        this.isBinging = true;
        this.interactionPointState = {
          countdownDuration: eventData.countdownDuration,
          seekToTime: eventData.seekToTime,
          visible: true,
          type: eventData.type,
          displayValue: eventData.displayValue,
          originalCountdownDuration: eventData.countdownDuration
        };
        this._countdownIntervalId = window.setInterval(() => {
          if (this.interactionPointState?.countdownDuration) {
            this.interactionPointState = {
              ...this.interactionPointState,
              countdownDuration: this.interactionPointState.countdownDuration - 1
            };
          }
          if (this.interactionPointState?.countdownDuration === 0) {
            window.clearInterval(this._countdownIntervalId);
          }
        }, 1e3);
      }
    } else if (event.type === "exit") {
      if (this.interactionPointState?.countdownDuration) {
        this.abortInteractionPoint();
        return this.playNext();
      }
      if (!this.interactionPointState?.manualExtension || this.interactionPointState?.manualExtension && this.currentTime < event.range.start) {
        this.interactionPointState = void 0;
        this.isBinging = false;
      }
    }
  }
  /**
   * Extend updateComplete if waiting for "metadata" since it is a derived value of "src"
   */
  get updateComplete() {
    return new Promise((resolve) => {
      super.updateComplete.then(() => {
        return this.pendingMetadataResponse;
      }).then(() => {
        resolve(this.isUpdatePending);
      });
    });
  }
  _onMediaInfoLoaded(event) {
    super._onMediaInfoLoaded(event);
    this.addEventsForInteractionPoints();
  }
  /**
   * Handle panicking errors dispatched by player or child elements
   */
  handlePanic(error) {
    super.handlePanic(error);
    this.suspend();
  }
  /**
   * Determine if this player can play given media type
   */
  canPlayType(_mediaType) {
    throw Error('PlayerElement subclass must implement "canPlayType()" method based on required PlayerCore adapters');
  }
  playerVersion() {
    return `${this.playerName}@${this.playerVersionNumber} (player-elements@${PLAYER_ELEMENTS_VERSION}/player-core@${playerCoreVersion})`;
  }
  abortInteractionPoint() {
    this.interactionPointState = void 0;
    window.clearInterval(this._countdownIntervalId);
  }
  resetNextUpCountdown() {
    if (this.interactionPointState) {
      this.interactionPointState.countdownDuration = this.interactionPointState.originalCountdownDuration;
    }
  }
  abortNextUpCountdown() {
    if (this.interactionPointState) {
      window.clearInterval(this._countdownIntervalId);
      this.interactionPointState.countdownDuration = void 0;
      this.interactionPointState.displayValue = "Neste";
      this.interactionPointState.manualExtension = true;
      this.isBinging = false;
    }
  }
  /**
   * Retrieve @nrk/player-core module and instantiate 'player' instance
   */
  async initPlayerCoreInstance() {
    throw Error('PlayerElement subclass must implement "initPlayerCoreInstance()" method');
  }
  async createSession() {
    await this.destroySession();
    if (!this.resolvedSrc) {
      return this.panic(
        new PlayerError(
          PlayerErrorTypes.MISSING_SOURCE,
          true,
          new Error("Avspilling feilet p\xE5 grunn av manglende mediakilde")
        )
      );
    }
    const mediaType = (this.resolvedType ? { type: this.resolvedType } : guessMediaTypeOfUrl(this.resolvedSrc)) ?? {
      type: "video/mp4"
    };
    if (!this.canPlayType(mediaType)) {
      return this.panic(
        new PlayerError(
          PlayerErrorTypes.SOURCE_NOT_SUPPORTED,
          true,
          new Error("Avspilling av mediainnholdet er ikke st\xF8ttet p\xE5 denne enheten"),
          { mediaId: this.src, mediaUrl: this.resolvedSrc }
        )
      );
    }
    try {
      const initialState = {
        mute: this.muted,
        startTime: this.metadata?.isChannel ? void 0 : this.startTimeOndemand,
        startTimeLive: this.metadata?.isChannel ? this.startTimeLive : void 0,
        videoPoster: this.poster
      };
      const mediaId = this.src !== this.resolvedSrc ? this.src : void 0;
      await this.player.createPlaybackSession(
        {
          id: mediaId,
          url: this.resolvedSrc,
          mediaType,
          // @ts-expect-error: Used by cast-player-core...
          customData: { preferredManifest: this.preferredManifest }
        },
        initialState
      );
    } catch (err) {
      this.panic(err);
    }
  }
  destroySession() {
    if (this.activeSession) {
      const session = this.activeSession;
      this._unobservePlaybackSession(session);
      this.epgState?.destroy();
      this.epgState = void 0;
      return session.close();
    }
    return Promise.resolve();
  }
  /**
   * Load metadata from PSAPI
   */
  async loadMetadata() {
    if (!this.psapiClient) {
      this.panic(Error(`Unable to load metadata for invalid PSAPI media reference: ${this.src}`));
      return;
    }
    if (this.metadata) {
      return;
    }
    if (this.pendingMetadataPlayable) {
      return this.pendingMetadataPlayable;
    }
    const iterator = this.psapiClient.loadMetadata({ throwNonPlayableError: true });
    let resolvePlayability;
    this.pendingMetadataPlayable = new Promise((resolve) => resolvePlayability = resolve);
    const iterate = () => {
      this.pendingMetadataResponse = iterator.next().then((result) => {
        this.pendingMetadataResponse = void 0;
        if (!result.done) {
          this.metadata = parseMetadata(result.value, this.startTimeOndemand);
          iterate();
        } else {
          resolvePlayability();
          this.pendingMetadataPlayable = void 0;
        }
      }).catch((err) => {
        this.pendingMetadataPlayable = void 0;
        this.pendingMetadataResponse = void 0;
        if (toError(err).name !== "AbortError") {
          this.panic(err);
        }
      });
    };
    iterate();
    return this.pendingMetadataPlayable;
  }
  async loadManifest(manifestType) {
    if (!this.psapiClient) {
      this.panic(Error(`Unable to load manifest for invalid PSAPI media reference: ${this.src}`));
      return;
    }
    if (this.pendingMetadataPlayable) {
      await this.pendingMetadataPlayable;
    }
    try {
      const manifestResponse = await this.psapiClient.updateManifest(manifestType);
      this.manifest = parseManifest(manifestResponse.data);
    } catch (err) {
      if (toError(err).name !== "AbortError") {
        this.panic(err);
      }
    }
  }
  /**
   * Unlock the media element, but check for the presence of one first.
   * Autoplay policy on ios safari needs this to not get a autoplayblocked on first click
   */
  unlockAudioVideoElement() {
    const element = this.querySelector("video,audio");
    if (element) {
      unlockMediaElement(element);
    }
  }
  adjustFontSize() {
    this.style.setProperty("--player-base-font-size", this.getRemFontSize());
  }
  /**
   * Find an appropriate font size based on the size of the player.
   * Scaling factor is 2 for small players, 1.25 for large players
   * Find a scaled base font size for the root element and divide it by the
   * actual font size of the root element, to take the user preference into account
   */
  getRemFontSize() {
    const scale = this.dimensions.width < FONT_SIZE_BREAK_POINT ? 2 : 5 / 4;
    const absoluteSize = scale * this.baseFontSize();
    const relativeSize = absoluteSize / getFontSize(document.documentElement);
    return `${relativeSize}rem`;
  }
  /**
   * The base font size is relative to the width of the largest possible 16:9
   * rectangle inside the the player. At 800px width, the font size is 1rem (in
   * pixels).
   *
   * The reference size could be 1rem at any width, but 800px seems to provide
   * a reasonable base size to work with.
   *
   * Using the root font size enables scaling of the text inside Ludo by
   * adjusting the root font size. This is for accessibility.
   */
  baseFontSize() {
    const maxAspectRatio = 1 / 0.5625;
    const referenceSize = getFontSize(document.documentElement) / 800;
    const baseWidth = Math.min(this.dimensions.width, this.dimensions.height * maxAspectRatio);
    return baseWidth * referenceSize;
  }
  get preferredManifestType() {
    const preferredManifest = this.metadata?.manifests?.find((v) => v.type === this.preferredManifest);
    if (preferredManifest) {
      return preferredManifest.type;
    } else if (this.src.match(/TOLK/)) {
      return "tegntolk";
    } else if (this.src.match(/SYNS/)) {
      return "synstolk";
    }
    return "default";
  }
  addEventsForInteractionPoints() {
    if (this.metadata?.interactionPoints?.seekToPoints && this.activeSession) {
      const timeRangeEvents = this.activeSession.createTimeRangeEventTrack();
      for (const seekToPoint of this.metadata.interactionPoints.seekToPoints) {
        this.logger.log("adding TimeRangeEvent for seekToPoint", seekToPoint);
        const startTime = seekToPoint.startTime === 0 ? this.startupSkipPointDelay : seekToPoint.startTime;
        const range = timeRangeEvents?.addRange(
          { start: startTime, end: seekToPoint.endTime },
          {
            type: "seekToPoint",
            displayValue: seekToPoint.displayValue,
            seekToTime: seekToPoint.seekToTime,
            autoskipInBingeMode: seekToPoint.autoSkipInBingeMode
          }
        );
        range?.on("enter", this.interactionPointHandler);
        range?.on("exit", this.interactionPointHandler);
      }
      if (this.metadata?.interactionPoints?.nextUpPoint && this.metadata.nextSrc) {
        const nextUpPoint = this.metadata.interactionPoints.nextUpPoint;
        this.logger.info("adding TimeRangeEvent for nextUpPoint", nextUpPoint);
        const range = timeRangeEvents?.addRange(
          { start: nextUpPoint.startTime, end: this.metadata.duration },
          {
            type: "nextUpPoint",
            displayValue: "Neste om",
            countdownDuration: nextUpPoint.countDownDuration
          }
        );
        range?.on("enter", this.interactionPointHandler);
        range?.on("exit", this.interactionPointHandler);
      }
    }
  }
  playNext() {
    if (this.metadata?.nextSrc) {
      this.dispatchEvent(
        new CustomEvent(PlayerEvents.PlayNextEpisode, {
          bubbles: true,
          cancelable: true,
          detail: {
            src: this.metadata?.nextSrc
          }
        })
      );
      this.interactionPointState = void 0;
      this.isBinging = true;
    }
  }
  getAuthToken() {
    if (this.user?.id) {
      return this.user.getAuthToken().then((token) => {
        return Promise.resolve(token);
      }).catch((_error) => {
        return Promise.resolve(void 0);
      });
    }
    return Promise.resolve(void 0);
  }
  // for easier debugging/testing, throwing a random error to enclosing context
  fakeError(type, fatal = false) {
    this.dispatchEvent(
      new CustomEvent(PlayerEvents.PlayerError, {
        bubbles: true,
        cancelable: true,
        detail: new PlayerError(
          type,
          fatal,
          type ? new Error(`FakeError ${type}`) : new Error(
            `fakeError(errorType: string | PlayerErrorTypes, fatal = false)
Throw an error out of the player to any listening context. Error types can be:
- ${Object.keys(
              PlayerErrorTypes
            ).join("\n- ")}
`
          )
        )
      })
    );
  }
};
function parseMetadata(metadata, startTime) {
  const images = metadata.preplay.poster.images;
  const available = metadata.playability === "playable";
  const availability = parseAvailability(metadata);
  const posterSrcset = generateSrcSet(images);
  const accessabilityVersions = metadata._embedded.manifests?.map((m) => {
    return { id: m.id, label: m.availabilityLabel, type: m._links.self.name };
  });
  const manifestSubtitles = metadata._embedded.manifestSubtitles?.map((manifest) => ({
    id: manifest.id,
    availabilityLabel: manifest.availabilityLabel,
    href: manifest._links.self.href,
    type: manifest._links.self.name
  }));
  const manifests = [].concat(
    accessabilityVersions || [],
    manifestSubtitles || []
  );
  const nextSrc = parseNext(metadata);
  return {
    availability: available ? { available: true } : {
      available: false,
      ...availability
    },
    description: metadata.preplay.description,
    duration: metadata.duration ? iso86012.toSeconds(iso86012.parse(metadata.duration)) : 0,
    heading: metadata.preplay.titles.title,
    id: metadata.id,
    isLive: metadata.streamingMode === "live",
    isChannel: metadata.availability.live?.type === "channel",
    legalAge: metadata.legalAge,
    startTime,
    posterSrcset,
    nextSrc,
    subheading: metadata.preplay.titles.subtitle,
    standaloneProgram: metadata._links.series || metadata.availability.live?.type === "channel" ? false : true,
    interactionPoints: metadata.interactionPoints,
    indexPoints: parseIndexPoints(metadata.preplay.indexPoints),
    manifests,
    accessabilityVersions,
    manifestSubtitles
  };
}
function parseNext(metadata) {
  if (!metadata._embedded.next) {
    return void 0;
  }
  const url = metadata._embedded.next._links.self.href;
  const id = url.substring(url.lastIndexOf("/") + 1);
  return `nrk:program:${id}`;
}
function parseIndexPoints(indexPoints) {
  return indexPoints.map((point) => {
    return {
      title: point.title,
      startPoint: durationToSeconds(point.startPoint),
      webImages: point.thumbnails?.map((t) => {
        return { uri: t.url, width: t.pixelWidth };
      })
    };
  });
}
function parseAvailability(metadata) {
  const { live, onDemand } = metadata.availability || {};
  const availability = {};
  if (live && live.transmissionInterval) {
    availability.from = new Date(live.transmissionInterval.from);
    availability.to = new Date(live.transmissionInterval.to);
  }
  if (onDemand) {
    availability.from = new Date(onDemand.from);
    availability.to = new Date(onDemand.to);
  }
  if (metadata.nonPlayable) {
    availability.reason = metadata.nonPlayable.reason;
    availability.endUserMessage = metadata.nonPlayable.endUserMessage;
    availability.messageType = metadata.nonPlayable.messageType;
  }
  availability.isPending = availability.from ? Date.now() < availability.from.getTime() : false;
  return availability;
}
function parseManifest(manifest) {
  if (manifest.playable) {
    const thumbnails = manifest.playable.thumbnails ?? [];
    const thumbnail2D = manifest.playable.thumbnail2D ?? void 0;
    return {
      href: manifest._links.self.href,
      statistics: manifest.statistics,
      playable: manifest.playable,
      thumbnails,
      thumbnail2D
    };
  }
}
function getSourceFromManifest(manifest) {
  const assets = manifest.playable.assets;
  if (assets && assets.length) {
    const asset = assets[0];
    return { src: asset.url, type: asset.mimeType };
  }
}
function getSourceFromElement(element) {
  for (const { src, type } of Array.from(element.querySelectorAll("source"))) {
    if (element.canPlayType({ type })) {
      return { src, type };
    }
  }
}
function isDate(value) {
  return isNaN(Number(value));
}

// src/utils/safe-ce-define.js
function safeCEDefine(tagName, constructor) {
  if (!customElements.get(tagName)) {
    customElements.define(tagName, constructor);
  }
}

// src/controls/controls-element.js
import { ReactiveElement as ReactiveElement2, ReactiveState as ReactiveState4 } from "@nrk/reactive-element";
import { throttle } from "throttle-debounce";
import { AutofocusController } from "@nrk/element-autofocus-controller";
var ControlsElement = class extends ReactiveElement2 {
  static get reactiveAttributes() {
    return {
      ...super.reactiveAttributes,
      active: Boolean
    };
  }
  static get reactiveProperties() {
    return {
      ...super.reactiveProperties,
      fullscreenState: Object,
      isCurrentTimeLive: Boolean,
      isEnded: Boolean,
      isLive: Boolean,
      isPlaying: Boolean,
      remotePlaybackState: Object,
      subtitleState: ReactiveState4,
      interactionPointState: Object,
      pipState: Object,
      isStarted: Boolean,
      muted: Boolean
    };
  }
  /** Flag indicating whether the button bar is active (visible) */
  active = false;
  /** The current Fullscreen state reported by Player-Core (shadowed Player property) */
  fullscreenState;
  /** Flag indication we are watching the stream live (shadowed Player property) */
  isCurrentTimeLive = false;
  /** Flag indicating that media playback has completed (shadowed Player property) */
  isEnded = false;
  /** Flag indicating whether the media source is live or on-demand (shadowed Player property) */
  isLive = false;
  /** Flag indicating that media playback has started (shadowed Player property) */
  isPlaying = false;
  /** Flag indicating that media playback has started (shadowed Player property) */
  isStarted = false;
  /** shadowed interactionState from player */
  interactionPointState;
  /** remotePlaybackState shadowed from player */
  remotePlaybackState;
  /** All things subtitles (shadowed Player property) */
  subtitleState;
  /** Picture-in-picture state, shadowed from player */
  pipState;
  /** Flag indicating that audio is muted (shadowed Player property) */
  muted = false;
  /** Allow this element to be source of click events that trigger a "TogglePlay" action */
  action = PlayerActions.TogglePlay;
  /** Hide until playback started */
  hidden = true;
  activatedAt;
  /** Flag to determine if the interaction was triggered by an input device that supports hover */
  isHovering = true;
  /** Flag to determine if pointer is outside controls area */
  isPointerOutside = false;
  /** Flag to determine if user is currently pressing with input device */
  isPressing = false;
  /** The PlayerElement parent instance */
  player;
  /** Time in milliseconds until active controls will automatically deactivate */
  deactivateTimeout = 2e3;
  _deactivateTimeoutId;
  _throttledHandleEvent = throttle(60, this.handleEvent);
  _unshadowProperties;
  constructor() {
    super();
    this.deactivate = this.deactivate.bind(this);
    new AutofocusController(this, {
      shouldAutofocus: () => {
        return this.hasUpdated("isConnected") || this.hasUpdated("hidden") || this.active && this.hasUpdated("active");
      }
    });
  }
  readyCallback() {
    this.tabIndex = 0;
    this.player = getElementOfType(this);
    this.addEventListener("click", this);
    this.addEventListener("focus", this, true);
    this.addEventListener("blur", this, true);
    this.addEventListener(
      "touchstart",
      this.handleTouchStartHoverProxyEvent,
      platform_default.supportsPassiveEvents ? { passive: true } : void 0
    );
    this.addEventListener(platform_default.pointerEventTypes.enter, this);
    this.addEventListener(platform_default.pointerEventTypes.down, this);
    this.addEventListener(platform_default.pointerEventTypes.move, this._throttledHandleEvent);
    this._unshadowProperties = this.player.shadowProperties(
      [
        "fullscreenState",
        "pipState",
        "isCurrentTimeLive",
        "isEnded",
        "isLive",
        "isPlaying",
        "isStarted",
        "interactionPointState",
        "muted",
        "subtitleState"
      ],
      this
    );
  }
  disconnectedCallback() {
    this.removeEventListener("click", this);
    this.removeEventListener("focus", this, true);
    this.removeEventListener("blur", this, true);
    this.removeEventListener("touchstart", this.handleTouchStartHoverProxyEvent);
    this.removeEventListener(platform_default.pointerEventTypes.enter, this);
    this.removeEventListener(platform_default.pointerEventTypes.down, this);
    this.removeEventListener(platform_default.pointerEventTypes.move, this._throttledHandleEvent);
    this.removeEventListener(platform_default.pointerEventTypes.leave, this);
    document.removeEventListener(platform_default.pointerEventTypes.up, this);
    this._unshadowProperties?.();
    this.player = null;
    super.disconnectedCallback();
  }
  updating(propName, oldValue) {
    super.updating(propName, oldValue);
    if (propName === "isStarted" && this.isStarted) {
      this.hidden = false;
    }
  }
  handleEvent(event) {
    const { target, type } = event;
    if (type === "focus") {
      this.activate(true, type);
    } else if (type === "blur") {
      const relatedTarget = event.relatedTarget;
      if (this.isPlaying && (!relatedTarget || !this.contains(relatedTarget))) {
        this.activate(false, type);
      }
    } else if (type === "click") {
      if (getActionElement(target) !== this) {
        this.activate(true, "click nested actionElement");
      } else if (!this.isHovering) {
        const msSinceLastActivation = (/* @__PURE__ */ new Date()).getTime() - (this.activatedAt || 0);
        if (msSinceLastActivation > 100) {
          this.activate(!this.active, type);
        }
        event.preventDefault();
      }
      this.isHovering = true;
    } else {
      this.handlePointerEvent(event);
    }
  }
  handleTouchStartHoverProxyEvent() {
    this.isHovering = false;
  }
  handlePointerEvent(event) {
    const { target, type } = event;
    const isCasting = Boolean(this.player?.activeSession?.getAdapterInfo().chromecast) ?? false;
    if (type === platform_default.pointerEventTypes.enter) {
      this.isPointerOutside = false;
      this.isPressing = false;
      this.addEventListener(platform_default.pointerEventTypes.move, this._throttledHandleEvent);
      this.addEventListener(platform_default.pointerEventTypes.leave, this);
    } else if (type === platform_default.pointerEventTypes.move) {
      if (!this.isPointerOutside) {
        this.activate(true, type);
      }
    } else if (type === platform_default.pointerEventTypes.down) {
      this.isPressing = true;
      this.removeEventListener(platform_default.pointerEventTypes.move, this._throttledHandleEvent);
      this.removeEventListener(platform_default.pointerEventTypes.leave, this);
      document.addEventListener(platform_default.pointerEventTypes.up, this);
    } else if (type === platform_default.pointerEventTypes.up) {
      this.isPressing = false;
      const isReleaseOutside = !this.contains(target);
      if (isReleaseOutside) {
        this.isHovering = true;
      } else if (this.isHovering) {
        this.addEventListener(platform_default.pointerEventTypes.move, this._throttledHandleEvent);
        this.addEventListener(platform_default.pointerEventTypes.leave, this);
      }
      if (this.isHovering || this.player.isSeeking) {
        this.activate(!isReleaseOutside, type);
      }
      document.removeEventListener(platform_default.pointerEventTypes.up, this);
    } else if (type === platform_default.pointerEventTypes.leave) {
      this.isPointerOutside = true;
      this.removeEventListener(platform_default.pointerEventTypes.move, this._throttledHandleEvent);
      this.removeEventListener(platform_default.pointerEventTypes.leave, this);
      this.activate(isCasting || !this.isPlaying || this.player.focusVisible, type);
    }
  }
  /**
   * Set "active" state, triggering a delayed auto-deactivation in certain cases.
   * Note that this is handled outside of normal update lifecycle in order
   * to be able to delay auto-deactivation by repeatedly setting "active=true".
   */
  activate(value, _reason) {
    window.clearTimeout(this._deactivateTimeoutId);
    this.active = value;
    if (this.active) {
      this._deactivateTimeoutId = window.setTimeout(this.deactivate, this.deactivateTimeout);
      this.activatedAt = (/* @__PURE__ */ new Date()).getTime();
    }
  }
  deactivate() {
    const shouldDeactivate = this.isPlaying && !this.isPressing && !this.player.focusVisible;
    const isCasting = Boolean(this.player?.activeSession?.getAdapterInfo().chromecast) ?? false;
    if (shouldDeactivate && !isCasting) {
      this.active = false;
    }
  }
};

// src/keyboard/keyboard-element.js
import { ReactiveElement as ReactiveElement3 } from "@nrk/reactive-element";
var KeyboardElement = class extends ReactiveElement3 {
  /** The PlayerElement parent instance */
  player;
  value;
  readyCallback() {
    this.player = getElementOfType(this);
    document.addEventListener("keydown", this);
  }
  disconnectedCallback() {
    document.removeEventListener("keydown", this);
    this.player = null;
    super.disconnectedCallback();
  }
  handleEvent(event) {
    if (!this.player.focusWithin || event.defaultPrevented) {
      return;
    }
    const key = event.key;
    const metaKey = event.metaKey || event.ctrlKey || event.altKey || event.shiftKey;
    const target = event.target;
    switch (key) {
      case "ArrowLeft": {
        if (!target || target.nodeName !== "INPUT") {
          event.preventDefault();
          this.emit(
            PlayerActions.SeekToRelativeTime,
            metaKey ? -this.player.seekableStep * 4 : -this.player.seekableStep
          );
        }
        break;
      }
      case "ArrowRight": {
        if (!target || target.nodeName !== "INPUT") {
          event.preventDefault();
          this.emit(
            PlayerActions.SeekToRelativeTime,
            metaKey ? this.player.seekableStep * 4 : this.player.seekableStep
          );
        }
        break;
      }
    }
    if (metaKey) {
      return;
    }
    switch (key) {
      case "Enter":
      case "SpaceBar":
      case " ": {
        if (target?.nodeName !== "BUTTON") {
          event.preventDefault();
          this.emit(PlayerActions.TogglePlay);
        }
        break;
      }
      case "k": {
        event.preventDefault();
        this.emit(PlayerActions.TogglePlay);
        break;
      }
      case "j": {
        event.preventDefault();
        this.emit(PlayerActions.SeekToRelativeTime, -this.player.seekableStep);
        break;
      }
      case "l": {
        event.preventDefault();
        this.emit(PlayerActions.SeekToRelativeTime, this.player.seekableStep);
        break;
      }
      case "0":
      case "1":
      case "2":
      case "3":
      case "4":
      case "5":
      case "6":
      case "7":
      case "8":
      case "9": {
        event.preventDefault();
        this.emit(PlayerActions.SeekToPosition, (this.keyToCode(key) - 48) / 10);
        break;
      }
      case "Home": {
        event.preventDefault();
        this.emit(PlayerActions.SeekToPosition, 0);
        break;
      }
      case "End": {
        event.preventDefault();
        this.emit(PlayerActions.SeekToPosition, 1);
        break;
      }
      case "ArrowUp": {
        if (!target || target.nodeName !== "INPUT") {
          event.preventDefault();
          console.log("dispatch for volume up", this.player.volume);
          this.emit(PlayerActions.SetVolume, this.player.volume * 100 + 10);
        }
        break;
      }
      case "ArrowDown": {
        if (!target || target.nodeName !== "INPUT") {
          event.preventDefault();
          this.emit(PlayerActions.SetVolume, this.player.volume * 100 - 10);
        }
        break;
      }
      case "f": {
        if (this.player.fullscreenState?.isActive) {
          this.emit(PlayerActions.ExitFullscreen);
        } else {
          this.emit(PlayerActions.EnterFullscreen);
        }
        break;
      }
      case "m": {
        event.preventDefault();
        this.emit(PlayerActions.ToggleMute);
        break;
      }
      case "c": {
        break;
      }
      case "p": {
        event.preventDefault();
        if (this.player.pipState?.isActive) {
          this.emit(PlayerActions.ExitPip);
        } else {
          this.emit(PlayerActions.EnterPip);
        }
        break;
      }
      case "a":
      case "z": {
        break;
      }
    }
  }
  keyToCode(key) {
    return key.charCodeAt(0);
  }
  emit(action, value) {
    this.value = value;
    this.setAttribute("action", action);
    this.dispatchEvent(new Event("change", { bubbles: true }));
  }
};

// src/poster/poster-element.js
import { ReactiveElement as ReactiveElement4 } from "@nrk/reactive-element";
var PosterElement = class extends ReactiveElement4 {
  static get reactiveAttributes() {
    return {
      heading: String,
      simple: Boolean,
      src: String,
      srcset: String,
      subheading: String,
      hidden: Boolean
    };
  }
  static get reactiveProperties() {
    return {
      availability: Object,
      dimensions: (element, _dimensions) => {
        if (element.srcsetConditions) {
          element.src = element.matchSrcset(element.srcsetConditions);
        }
      },
      duration: Number,
      isLive: Boolean,
      isTimeRunning: (element, isTimeRunning) => {
        if (isTimeRunning) {
          element.hidden = true;
        }
      },
      legalAgeText: String,
      metadata: (element, metadata) => {
        if (metadata) {
          element.copyMetadata(metadata);
        }
      },
      muted: Boolean,
      startTime: Number,
      srcset: String,
      videoSrc: (element, _videoSrc) => {
        if (!element.player.isBinging) {
          element.hidden = false;
        }
      }
    };
  }
  /** Availability data for the src */
  availability;
  /** Player dimensions (shadowed Player property) */
  dimensions;
  /** The media duration in seconds */
  duration = 0;
  /** The heading text */
  heading;
  /** Flag indicating whether the media is live or on-demand */
  isLive;
  /** Flag indicating that media playback is ongoing (shadowed Player property) */
  isTimeRunning;
  /** The legal age designation */
  legalAgeText;
  /** The legal age to display (9+, 16+ etc) */
  legalDisplayAge;
  /** Metadata for the current Player source (shadowed Player property) */
  metadata;
  /** Flag indicating that audio is muted (shadowed Player property) */
  muted;
  /** Attribute flag indicating simple layout mode */
  simple = false;
  /** The initial playhead position in seconds */
  startTime;
  /** The image source reference */
  src;
  /** The image srcset reference */
  srcset;
  /** The subheading text */
  subheading;
  /** The player source (src) (shadowed Player property) */
  videoSrc;
  /** The PlayerElement parent instance */
  player;
  manualOverrides = {
    srcset: false,
    heading: false,
    subheading: false
  };
  srcsetConditions;
  _unshadowProperties;
  readyCallback() {
    this.player = getElementOfType(this);
    this._unshadowProperties = this.player.shadowProperties(
      ["dimensions", "isTimeRunning", "metadata", "muted", "src:videoSrc"],
      this
    );
    if (!this.isBootstrapped) {
      this.manualOverrides = {
        srcset: this.srcset !== void 0,
        heading: this.heading !== void 0,
        subheading: this.subheading !== void 0
      };
    }
  }
  updating(propName, oldValue) {
    super.updating(propName, oldValue);
    if (propName === "srcset" && this.srcset) {
      this.srcsetConditions = parseSrcset(this.srcset);
      this.src = this.matchSrcset(this.srcsetConditions);
    }
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (!this.isConnected) {
      return;
    }
    if (this.src && changedProperties.has("src")) {
      this.player.poster = this.src;
    }
  }
  disconnectedCallback() {
    this._unshadowProperties?.();
    this.player = null;
    super.disconnectedCallback();
  }
  copyMetadata(metadata) {
    const legalAgeBody = metadata.legalAge?.body;
    this.duration = metadata.duration;
    this.isLive = metadata.isLive;
    this.availability = metadata.availability;
    this.startTime = metadata.startTime;
    if (legalAgeBody && "rating" in legalAgeBody) {
      this.legalAgeText = legalAgeBody.rating.displayValue;
      this.legalDisplayAge = legalAgeBody.rating.displayAge;
    }
    if (!this.manualOverrides.srcset && metadata.posterSrcset) {
      this.srcset = metadata.posterSrcset;
    }
    if (!this.manualOverrides.subheading && metadata.subheading) {
      this.subheading = metadata.subheading;
    }
    if (!this.manualOverrides.heading && metadata.heading) {
      this.heading = metadata.heading;
    }
  }
  /**
   * Retrieve a descriptive text string derived from state
   */
  get accessibleDescription() {
    const lines = [];
    const readableDuration = !this.isLive && durationToReadableString(this.duration);
    if (this.isLive) {
      lines.push("Direkte n\xE5");
    }
    if (this.heading) {
      lines.push(`Spill av ${this.heading}`);
    }
    if (this.legalAgeText) {
      lines.push(this.legalAgeText);
    }
    if (readableDuration) {
      lines.push(`Varigheten er ${readableDuration}`);
    }
    return lines.length ? `${lines.join(". ")}.` : "";
  }
  matchSrcset(srcsetConditions) {
    const { width } = this.getBoundingClientRect();
    return matchSrcset({
      srcsetConditions,
      devicePixelRatio: window.devicePixelRatio,
      elementWidth: width
    }) ?? this.src;
  }
};

// src/scrubber/scrubber-element.js
import { ReactiveElement as ReactiveElement5 } from "@nrk/reactive-element";

// src/scrubber/scrubber-render-mixin.js
var CSS_TRANSFORM_PROPERTY = "transform" in document.head.style ? "transform" : "webkitTransform";
var MS_PER_PIXEL_ANIM_CUTOFF = 500;
var TWENTY_FOUR_HRS_IN_SECONDS = 86400;
function ScrubberRenderMixin(Base) {
  return class extends Base {
    static get reactiveAttributes() {
      return {
        ...super.reactiveAttributes,
        active: Boolean
      };
    }
    static get reactiveProperties() {
      return {
        ...super.reactiveProperties,
        endTime: Number,
        // Seconds
        isDragging: Boolean,
        isLive: Boolean,
        isRealtime: Boolean,
        isSeeking: Boolean,
        isTimeRunning: Boolean,
        playbackRate: Number,
        playheadPosition: Number,
        // 0-1
        pointerPosition: Number,
        // 0-1
        seekableStep: Number,
        // Seconds
        startTime: Number,
        // Seconds
        width: Number
      };
    }
    /**
     * Flag indicating whether active (visible).
     * In general, we disable element animation when not active.
     * For accessibility reasons, "hidden" cannot be used because elements must be
     * available at all times when using screen readers.
     */
    active = false;
    /** The seekable end time in seconds */
    endTime = 0;
    /** Flag to determine if currently dragging with pointer  */
    isDragging = false;
    /** Flag indicating that media playback is a live or event stream (shadowed Player property) */
    isLive = false;
    /** Flag indicating that media playback is a actually live at the given time (shadowed Player property) */
    isRealtime = false;
    /** Flag indicating that we are seeking for new media playback position  (shadowed Player property) */
    isSeeking = false;
    /** Flag indicating that media playback is ongoing (shadowed Player property) */
    isTimeRunning = false;
    /** Playback speed where 1.0 is "normal" (shadowed Player property) */
    playbackRate = 1;
    /** The current playhead position from 0-1 */
    playheadPosition = 0;
    /** The current pointer position from 0-1, or "undefined" if no pointer active */
    pointerPosition;
    /** The seekable step size in seconds (shadowed Player property) */
    seekableStep = 1;
    /** The seekable start time in seconds */
    startTime = 0;
    /** The current element width */
    width = 0;
    /** The program start time in seconds from epoch for live media (shadowed Player property) */
    epochStartTime = 0;
    /** The PlayerElement parent instance */
    player;
    _unshadowProperties;
    _elPlayheadTime = null;
    _elPlayheadTimeComputedStyles;
    _elEndTime = null;
    _elInputRange = null;
    _elPlayhead = null;
    _elPlayheadComputedStyles;
    _elProgressBarPlayed = null;
    _elProgressBarPlayedComputedStyles;
    _elPointerTime = null;
    _elStartTime = null;
    _elIndexPoints = null;
    _elScrubberImage = null;
    readyCallback() {
      this.player = getElementOfType(this);
      this.selectElements();
      this.width = this.offsetWidth;
      this._unshadowProperties = this.player.shadowProperties(
        ["isLive", "isRealtime", "isSeeking", "isTimeRunning", "playbackRate", "seekableStep"],
        this
      );
    }
    updating(propName, oldValue) {
      if (this.width !== this.offsetWidth) {
        this.width = this.offsetWidth;
      }
      if (propName === "active") {
        if (this.active && !this.width) {
          this.width = this.offsetWidth;
        }
      } else if (propName === "isDragging") {
        if (!this.isDragging && this.pointerPosition !== void 0) {
          this.isTimeRunning = false;
          this.playheadPosition = this.pointerPosition;
        }
      }
      super.updating(propName, oldValue);
    }
    updated(changedProperties) {
      super.updated(changedProperties);
      if (!this.endTime) {
        return;
      }
      const duration = this.endTime - this.startTime;
      if (changedProperties.has("pointerPosition")) {
        this.updatePointerTimeElement(duration);
      }
      if (changedProperties.has("active") || changedProperties.has("endTime") || changedProperties.has("startTime") || changedProperties.has("isTimeRunning") || changedProperties.has("playheadPosition") || changedProperties.has("playbackRate") || changedProperties.has("seekableStep") || changedProperties.has("width")) {
        const playheadTime = duration * this.playheadPosition;
        const playheadDisplayTime = this.isRealtime ? (this.epochStartTime + playheadTime) % TWENTY_FOUR_HRS_IN_SECONDS : playheadTime % TWENTY_FOUR_HRS_IN_SECONDS;
        const formattedPlayheadDisplayTime = formatDuration(playheadDisplayTime);
        const formattedDuration = formatDuration(duration);
        this.updateInputRangeElement(formattedPlayheadDisplayTime, formattedDuration, playheadTime);
        if (!this.active) {
          return;
        }
        const playheadX = Math.min(1, this.playheadPosition) * this.width;
        const msPerPixel = this.width ? duration * 1e3 * this.playbackRate / this.width : 0;
        const shouldAnimate = !this.isLive && (msPerPixel > 0 ? !platform_default.isIE && msPerPixel <= MS_PER_PIXEL_ANIM_CUTOFF : false);
        const animationDuration = msPerPixel * (this.width - playheadX);
        if (!this.isDragging) {
          this.updateProgressBarPlayedElement();
        }
        this.updateIndexPointsElement();
        this.updatePlayheadElement(shouldAnimate, animationDuration, playheadX);
        const [elPlayheadTimeLeftEdge, elPlayheadTimeRightEdge] = this.updatePlayheadTimeElement(
          formattedPlayheadDisplayTime,
          playheadX,
          shouldAnimate,
          animationDuration,
          playheadTime
        );
        this.updateStartTimeElement(elPlayheadTimeLeftEdge);
        this.updateEndTimeElement(playheadTime, elPlayheadTimeRightEdge);
      }
    }
    updateIndexPointsElement() {
      if (this._elIndexPoints) {
        this._elIndexPoints.setAttribute("endtime", this.endTime.toString());
        this._elIndexPoints.setAttribute(
          "currenttime",
          ((this.endTime - this.startTime) * this.playheadPosition).toString()
        );
      }
    }
    updateScrubberImageElement(_pointerX, _maxPointerX, _pointerTime) {
    }
    updatePointerTimeElement(duration) {
      if (this.pointerPosition !== void 0) {
        const pointerX = this.pointerPosition * this.width;
        const pointerTime = duration * this.pointerPosition;
        if (this._elPointerTime) {
          const pointerDisplayTime = this.isRealtime ? (this.epochStartTime + pointerTime) % TWENTY_FOUR_HRS_IN_SECONDS : pointerTime % TWENTY_FOUR_HRS_IN_SECONDS;
          this._elPointerTime.innerText = formatDuration(pointerDisplayTime);
          const width = this._elPointerTime.offsetWidth;
          const widthHalf = width / 2;
          const maxPointerX = this.width - width;
          this._elPointerTime.style.visibility = "visible";
          this._elPointerTime.style[CSS_TRANSFORM_PROPERTY] = `translateX(${Math.max(
            0,
            Math.min(pointerX - widthHalf, maxPointerX)
          )}px)`;
          this.updateScrubberImageElement(pointerX, maxPointerX, pointerDisplayTime);
        }
        if (this.isDragging) {
          if (this._elPlayhead) {
            this._elPlayhead.style[CSS_TRANSFORM_PROPERTY] = `translateX(${pointerX}px)`;
            this._elPlayhead.style.transitionDuration = "unset";
          }
          if (this._elInputRange) {
            this._elInputRange.value = `${pointerTime}`;
          }
          if (this._elProgressBarPlayed) {
            const playheadPosition = Math.min(1, this.pointerPosition);
            this._elProgressBarPlayed.style.width = `100%`;
            this._elProgressBarPlayed.style.transformOrigin = `center left`;
            this._elProgressBarPlayed.style.transform = `scaleX(${playheadPosition})`;
          }
        }
      } else if (this._elPointerTime) {
        this._elPointerTime.style.visibility = "hidden";
        if (this._elScrubberImage) {
          this._elScrubberImage.style.visibility = "hidden";
        }
      }
    }
    updateInputRangeElement(formattedPlayheadDisplayTime, formattedDuration, playheadTime) {
      if (this._elInputRange) {
        this._elInputRange.setAttribute("step", `${this.seekableStep}`);
        this._elInputRange.setAttribute("min", `${this.startTime}`);
        this._elInputRange.setAttribute("max", `${this.endTime}`);
        this._elInputRange.setAttribute(
          "aria-valuetext",
          this.isRealtime ? formattedPlayheadDisplayTime : `${formattedPlayheadDisplayTime} av ${formattedDuration}`
        );
        if (!this.isDragging) {
          this._elInputRange.value = `${playheadTime}`;
        }
      }
    }
    updateProgressBarPlayedElement() {
      const playheadPosition = Math.min(1, this.playheadPosition);
      if (this._elProgressBarPlayed) {
        this._elProgressBarPlayed.style.width = `100%`;
        this._elProgressBarPlayed.style.transformOrigin = `center left`;
        this._elProgressBarPlayed.style.transform = `scaleX(${playheadPosition})`;
        this._elProgressBarPlayed.style.transitionDuration = "unset";
      }
    }
    updatePlayheadElement(shouldAnimate, animationDuration, playheadX) {
      if (!this.isDragging && this._elPlayhead) {
        this._elPlayhead.style[CSS_TRANSFORM_PROPERTY] = `translateX(${playheadX}px)`;
        this._elPlayhead.style.transitionDuration = "unset";
        this._elPlayhead.style.willChange = "transform";
        if (this.isTimeRunning && shouldAnimate) {
          this._elPlayheadComputedStyles[CSS_TRANSFORM_PROPERTY];
          this._elPlayhead.style[CSS_TRANSFORM_PROPERTY] = `translateX(${this.width}px)`;
          this._elPlayhead.style.transitionDuration = `${animationDuration}ms`;
        }
      }
    }
    updatePlayheadTimeElement(formattedPlayheadDisplayTime, playheadX, shouldAnimate, animationDuration, _playheadTime) {
      let elPlayheadTimeLeftEdge = 0;
      let elPlayheadTimeRightEdge = 0;
      if (this._elPlayheadTime) {
        const width = this._elPlayheadTime.offsetWidth;
        const widthHalf = Math.round(width / 2);
        const boundsLeft = widthHalf;
        const boundsRight = this.width - (this._elPointerTime && this._elPointerTime?.offsetWidth / 2 > widthHalf ? this._elPointerTime?.offsetWidth / 2 : widthHalf);
        const xTarget = Math.min(Math.max(playheadX, boundsLeft), boundsRight);
        const isSticky = playheadX < widthHalf || playheadX > boundsRight;
        elPlayheadTimeLeftEdge = xTarget - widthHalf - 2;
        elPlayheadTimeRightEdge = xTarget + widthHalf + 2;
        this._elPlayheadTime.innerText = formattedPlayheadDisplayTime;
        this._elPlayheadTime.style.marginLeft = `-${widthHalf}px`;
        this._elPlayheadTime.style[CSS_TRANSFORM_PROPERTY] = `translateX(${xTarget}px)`;
        this._elPlayheadTime.style.transitionDuration = `unset`;
        if (!isSticky && this.isTimeRunning && shouldAnimate) {
          this._elPlayheadTimeComputedStyles[CSS_TRANSFORM_PROPERTY];
          this._elPlayheadTime.style[CSS_TRANSFORM_PROPERTY] = `translateX(${this.width}px)`;
          this._elPlayheadTime.style.transitionDuration = `${animationDuration}ms`;
        }
      }
      return [elPlayheadTimeLeftEdge, elPlayheadTimeRightEdge];
    }
    updateStartTimeElement(elPlayheadTimeLeftEdge) {
      if (this._elStartTime) {
        if (this.isRealtime) {
          const displayTime = this.isRealtime ? this.epochStartTime % TWENTY_FOUR_HRS_IN_SECONDS : this.startTime;
          this._elStartTime.innerText = `${formatDuration(displayTime)}`;
          this._elStartTime.style.visibility = elPlayheadTimeLeftEdge > this._elStartTime.offsetWidth ? "visible" : "hidden";
        } else {
          this._elStartTime.style.visibility = "hidden";
        }
      }
    }
    updateEndTimeElement(playheadTime, elPlayheadTimeRightEdge) {
      const duration = this.endTime - this.startTime;
      if (this._elEndTime) {
        const displayTime = this.isRealtime ? (this.epochStartTime + duration) % TWENTY_FOUR_HRS_IN_SECONDS : this.endTime;
        const formattedDisplayTime = formatDuration(this.isRealtime ? displayTime : playheadTime - this.endTime);
        this._elEndTime.innerText = `${this.isRealtime ? "" : "-"}${formattedDisplayTime}`;
        this._elEndTime.style.visibility = elPlayheadTimeRightEdge < this.width - this._elEndTime.offsetWidth ? "visible" : "hidden";
      }
    }
    selectElements() {
      if (this._elPlayheadTime) {
        this._elPlayheadTimeComputedStyles = getComputedStyle(this._elPlayheadTime);
      }
      if (this._elPlayhead) {
        this._elPlayheadComputedStyles = getComputedStyle(this._elPlayhead);
      }
      if (this._elProgressBarPlayed) {
        this._elProgressBarPlayedComputedStyles = getComputedStyle(this._elProgressBarPlayed);
      }
    }
    disconnectedCallback() {
      this._unshadowProperties?.();
      this.player = null;
      super.disconnectedCallback && super.disconnectedCallback();
    }
  };
}

// src/scrubber/scrubber-element.js
var ScrubberElement = class extends ScrubberRenderMixin(ReactiveElement5) {
  static get reactiveProperties() {
    return {
      ...super.reactiveProperties,
      dimensions: (element, _dimensions) => {
        element.width = element.offsetWidth;
      }
      // endTime - added by ScrubberRender mixin
      // isDragging - added by ScrubberRender mixin
      // isLive - added by ScrubberRender mixin
      // isRealtime - added by ScrubberRender mixin
      // isTimeRunning - added by ScrubberRender mixin
      // playbackRate - added by ScrubberRender mixin
      // playheadPosition - added by ScrubberRender mixin
      // pointerPosition - added by ScrubberRender mixin
      // seekableStep - added by ScrubberRender mixin
      // startTime - added by ScrubberRender mixin
      // width - added by ScrubberRender mixin
    };
  }
  /** The Player dimensions (shadowed Player property) */
  dimensions;
  _unshadowAdditionalProperties;
  readyCallback() {
    super.readyCallback();
    this.addEventListener(platform_default.pointerEventTypes.enter, this);
    this.addEventListener(platform_default.pointerEventTypes.move, this);
    this.addEventListener(platform_default.pointerEventTypes.down, this);
    this._elInputRange?.addEventListener("change", this);
    this.player.addEventListener(PlayerEvents.ScrubberUpdate, this);
    this._unshadowAdditionalProperties = this.player.shadowProperties(["dimensions"], this);
  }
  disconnectedCallback() {
    this.removeEventListener(platform_default.pointerEventTypes.enter, this);
    this.removeEventListener(platform_default.pointerEventTypes.move, this);
    this.removeEventListener(platform_default.pointerEventTypes.down, this);
    this.removeEventListener(platform_default.pointerEventTypes.leave, this);
    document.removeEventListener(platform_default.pointerEventTypes.up, this);
    this.player?.removeEventListener(PlayerEvents.ScrubberUpdate, this);
    this._elInputRange?.removeEventListener("change", this);
    this._unshadowAdditionalProperties?.();
    super.disconnectedCallback();
  }
  updating(propName, oldValue) {
    super.updating(propName, oldValue);
    if (propName === "isDragging") {
      if (this.isDragging) {
        this.classList.add("-scrubber--dragging");
      } else {
        this.classList.remove("-scrubber--dragging");
      }
    }
  }
  handleEvent(event) {
    const { type } = event;
    if (isPlayerEvent(event, PlayerEvents.ScrubberUpdate)) {
      const { playheadPosition, endPosition, startPosition, startLiveTime } = event.detail;
      this.endTime = endPosition;
      this.startTime = startPosition;
      this.playheadPosition = (playheadPosition - startPosition) / (endPosition - startPosition);
      if (startLiveTime) {
        this.epochStartTime = (startLiveTime.getTime() - startLiveTime.getTimezoneOffset() * 6e4) / 1e3;
      }
    } else if (type === platform_default.pointerEventTypes.enter) {
      this.addEventListener(platform_default.pointerEventTypes.leave, this);
    } else if (type === platform_default.pointerEventTypes.move) {
      this.pointerPosition = getPositionFromEvent(this, event);
    } else if (type === platform_default.pointerEventTypes.down) {
      this.isDragging = true;
      document.addEventListener(platform_default.pointerEventTypes.up, this);
    } else if (type === platform_default.pointerEventTypes.up) {
      const oldStep = this.seekableStep;
      this.seekableStep = 1;
      if (this.pointerPosition === void 0) {
        this.pointerPosition = getPositionFromEvent(this, event);
      }
      this.isDragging = false;
      this.pointerPosition = void 0;
      document.removeEventListener(platform_default.pointerEventTypes.up, this);
      this.updateComplete.then(() => {
        if (this._elInputRange) {
          if (!this.isSeeking) {
            this._elInputRange.value = `${(this.endTime - this.startTime) * this.playheadPosition + this.startTime}`;
            this._elInputRange.dispatchEvent(new Event("change", { cancelable: true, bubbles: true }));
          }
          this.seekableStep = oldStep;
        }
      });
    } else if (type === "change") {
      if (this.isSeeking) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    } else if (type === platform_default.pointerEventTypes.leave) {
      this.removeEventListener(platform_default.pointerEventTypes.leave, this);
      this.pointerPosition = void 0;
    }
  }
  selectElements() {
    this._elPlayheadTime = this.querySelector('[type="playhead-time"]');
    this._elEndTime = this.querySelector('[type="end-time"]');
    this._elInputRange = this.querySelector('input[type="range"]');
    this._elPlayhead = this.querySelector('[type="playhead"]');
    this._elProgressBarPlayed = this.querySelector('[type="progressbar-played"]');
    this._elPointerTime = this.querySelector('[type="pointer-time"]');
    this._elStartTime = this.querySelector('[type="start-time"]');
    this._elScrubberImage = this.querySelector('[type="scrubber-image"]');
    super.selectElements();
  }
};

// src/subtitles/subtitles-element.js
import { ReactiveElement as ReactiveElement6, ReactiveState as ReactiveState5 } from "@nrk/reactive-element";
import { DimensionsMixin as DimensionsMixin2 } from "@nrk/element-dimensions-mixin";

// src/utils/cue-position.js
var DEFAULT_LINE_MARGIN = 5;
var DEFAULT_INDENTATION = 11.5;
function computeCuePosition(settings, config) {
  const result = {
    styles: {},
    insertLineBreaks: 0,
    appendLineBreaks: 0
  };
  const textDirection = config?.textDirection ?? "ltr";
  const lineMargin = config?.lineMargin ?? DEFAULT_LINE_MARGIN;
  const { vertical, positionAlign, size } = settings;
  let { snapToLines, line, lineAlign, position, align } = settings;
  let computedLine = getComputedLine(line, snapToLines, lineMargin);
  let computedPositionAlignment = getComputedPositionAlignment(positionAlign ?? "auto", align, textDirection);
  if (snapToLines) {
    snapToLines = false;
    if (computedLine >= 0) {
      line = lineMargin;
      result.insertLineBreaks = computedLine;
    } else {
      line = 100 - lineMargin;
      result.appendLineBreaks = -computedLine - 1;
    }
    computedLine = getComputedLine(line, snapToLines, lineMargin);
  }
  if (lineAlign === void 0 || lineAlign === "start") {
    if (+line === 50) {
      lineAlign = "center";
    } else if (+line > 50) {
      lineAlign = "end";
    }
  }
  if (config?.defaultToIndentedStartAlignment && position === "auto" && (positionAlign === void 0 || positionAlign === "auto")) {
    const indentation = config?.indentation ?? DEFAULT_INDENTATION;
    switch (computedPositionAlignment) {
      case "line-left":
        position = indentation;
        break;
      case "center":
        position = indentation;
        align = "start";
        break;
      case "line-right":
        position = 100 - indentation;
        break;
    }
    computedPositionAlignment = getComputedPositionAlignment(positionAlign ?? "auto", align, textDirection);
  }
  const computedPosition = getComputedPosition(position, align);
  const writingMode = { "": "horizontal-tb", rl: "vertical-rl", lr: "vertical-lr" }[vertical];
  const maxSize = getMaxSize(computedPosition, computedPositionAlignment);
  const boundSize = Math.min(size, maxSize);
  const width = vertical === "" ? `${boundSize}%` : "auto";
  const height = vertical === "" ? "auto" : `${boundSize}%`;
  const xyPosition = getXYPosition(computedPosition, computedPositionAlignment, boundSize);
  let xPosition;
  let yPosition;
  if (vertical === "") {
    xPosition = xyPosition;
    yPosition = snapToLines ? 0 : computedLine;
  } else {
    xPosition = snapToLines ? 0 : computedLine;
    yPosition = xyPosition;
  }
  const left = `${xPosition}%`;
  const top = `${yPosition}%`;
  let transform = "none";
  if (snapToLines) {
  } else {
    if (vertical === "") {
      if (lineAlign === "center") {
        transform = "translateY(-50%)";
      } else if (lineAlign === "end") {
        transform = "translateY(-100%)";
      }
    } else {
      if (lineAlign === "center") {
        transform = "translateX(-50%)";
      } else if (lineAlign === "end") {
        transform = "translateX(-100%)";
      }
    }
  }
  const textAlign = getTextAlign(align, textDirection);
  result.styles.writingMode = writingMode;
  result.styles.top = top;
  result.styles.left = left;
  result.styles.width = width;
  result.styles.height = height;
  result.styles.transform = transform;
  result.styles.textAlign = textAlign;
  return result;
}
function getMaxSize(computedPosition, computedPositionAlignment) {
  if (computedPositionAlignment === "line-left") {
    return 100 - computedPosition;
  }
  if (computedPositionAlignment === "line-right") {
    return computedPosition;
  }
  if (computedPosition <= 50) {
    return computedPosition * 2;
  }
  return (100 - computedPosition) * 2;
}
function getXYPosition(computedPosition, computedPositionAlignment, size) {
  if (computedPositionAlignment === "line-left") {
    return computedPosition;
  }
  if (computedPositionAlignment === "center") {
    return computedPosition - size / 2;
  }
  return computedPosition - size;
}
function getComputedLine(line, snapToLines, lineMargin) {
  if (line !== "auto") {
    if (!snapToLines && (line < 0 || line > 100)) {
      return 100 - lineMargin;
    }
    return line;
  }
  if (!snapToLines) {
    return 100 - lineMargin;
  }
  return -1;
}
function getComputedPosition(position, align) {
  if (position !== "auto" && position >= 0 && position <= 100) {
    return position;
  }
  switch (align) {
    case "left":
      return 0;
    case "right":
      return 100;
    default:
      return 50;
  }
}
function getComputedPositionAlignment(positionAlign, align, textDirection) {
  if (positionAlign !== "auto") {
    return positionAlign;
  }
  switch (align) {
    case "left":
      return "line-left";
    case "right":
      return "line-right";
    case "start":
      return textDirection === "ltr" ? "line-left" : "line-right";
    case "end":
      return textDirection === "ltr" ? "line-right" : "line-left";
    default:
      return "center";
  }
}
function getTextAlign(align, textDirection) {
  if (align === "start") {
    return textDirection === "ltr" ? "left" : "right";
  }
  if (align === "end") {
    return textDirection === "ltr" ? "right" : "left";
  }
  return align;
}

// src/subtitles/subtitles-element.js
var SubtitlesElement = class extends DimensionsMixin2(ReactiveElement6) {
  static get reactiveProperties() {
    return {
      ...super.reactiveProperties,
      subtitleState: ReactiveState5,
      fullscreenState: Object,
      pipState: Object,
      remotePlaybackState: Object
    };
  }
  /** Player aspect ratio */
  playerAspectRatio;
  /* Video aspect ratio */
  videoAspectRatio;
  /** The subtitle state object (shadowed player property) */
  subtitleState;
  /** The fullscreen state object (shadowed player property) */
  fullscreenState;
  /** The picture-in-picture state object (shadowed player property) */
  pipState;
  /** The remote playback state object (shadowed player property) */
  remotePlaybackState;
  /** Avoid custom rendering when native rendering is enabled */
  nativeRenderingEnabled;
  /** Override to configure cue positioning */
  cueConfig = {};
  action = PlayerActions.Speak;
  player;
  value;
  activeCuePositions = [];
  _unshadowProperties;
  connectedCallback() {
    super.connectedCallback();
    this.player = getElementOfType(this);
    this.player.addEventListener(PlayerEvents.QualityChange, this);
    this._unshadowProperties = this.player.shadowProperties(["subtitleState", "fullscreenState"], this);
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.player?.removeEventListener(PlayerEvents.QualityChange, this);
    this._unshadowProperties?.();
    this.player = null;
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (!this.isConnected || !this.player) {
      return;
    }
    if (changedProperties.has("subtitleState") && this.subtitleState) {
      const cues = this.subtitleState.activeCues;
      const stripTags = (s) => s.replace(/<[/\w][^<]*>/g, "");
      const text = cues.map((cue) => stripTags(cue.text)).join(" ");
      if (text) {
        this.value = text;
        this.dispatchEvent(new Event("change", { cancelable: true, bubbles: true }));
      }
      this.activeCuePositions = cues.map((cue) => computeCuePosition(cue, this.cueConfig));
    }
    if (changedProperties.has("fullscreenState")) {
      const useNativeRendering = this.fullscreenState?.isActive && this.fullscreenState.isNativeUI;
      if (this.nativeRenderingEnabled !== useNativeRendering) {
        this.nativeRenderingEnabled = useNativeRendering;
        if (useNativeRendering) {
          this.player.activeSession?.enableNativeSubtitlesDisplay();
        } else {
          this.player.activeSession?.disableNativeSubtitlesDisplay();
        }
      }
    }
    if (changedProperties.has("dimensions")) {
      this.playerAspectRatio = this.player.clientWidth / this.player.clientHeight;
      this.updateRenderingArea();
    }
  }
  handleEvent(event) {
    if (isPlayerEvent(event, PlayerEvents.QualityChange)) {
      if (!this.videoAspectRatio && this.player.activeSession?.getCurrentQuality()) {
        const quality = this.player.activeSession.getCurrentQuality();
        if (quality?.width && quality.height) {
          this.playerAspectRatio = this.player.clientWidth / this.player.clientHeight;
          this.videoAspectRatio = quality.width / quality.height;
          this.updateRenderingArea();
        }
      }
    }
  }
  /**
   * Check this before rendering HTML elements. Don't render the subtitles
   * when:
   * - The subtitles are being rendered natively.
   * - The video is not displayed inside the player.
   */
  isRenderingEnabled() {
    return !this.nativeRenderingEnabled && !this.pipState?.isActive && !this.remotePlaybackState?.isActive;
  }
  /**
   * Make this element cover the visible area of the video, so that the
   * positioning of the subtitle cues don't need to worry about the size or
   * shape of the player.
   */
  updateRenderingArea() {
    const blackBars = this.getBlackBars();
    this.style.top = blackBars.top ?? "0px";
    this.style.right = blackBars.right ?? "0px";
    this.style.bottom = blackBars.bottom ?? "0px";
    this.style.left = blackBars.left ?? "0px";
  }
  /**
   * Calculate the "black bars" between the player and the video. Black bars in
   * the video itself are not accounted for.
   */
  getBlackBars() {
    const playerAspectRatio = this.playerAspectRatio;
    const videoAspectRatio = this.videoAspectRatio;
    if (playerAspectRatio === void 0 || videoAspectRatio === void 0) {
      return {};
    }
    if (playerAspectRatio === videoAspectRatio || this.getObjectFit() !== "contain") {
      return {};
    }
    if (playerAspectRatio > videoAspectRatio) {
      const blackBarSize = (1 - videoAspectRatio / playerAspectRatio) / 2 * 100;
      return {
        right: `${blackBarSize}%`,
        left: `${blackBarSize}%`
      };
    } else {
      const blackBarSize = (1 - playerAspectRatio / videoAspectRatio) / 2 * 100;
      return {
        top: `${blackBarSize}%`,
        bottom: `${blackBarSize}%`
      };
    }
  }
  getObjectFit() {
    const video = this.player?.querySelector("video");
    return video ? getComputedStyle(video).objectFit : "contain";
  }
};

// src/tracking/tracking-element.js
import { PlayerTracker } from "@nrk/player-tracker";
import { ReactiveElement as ReactiveElement7, ReactiveState as ReactiveState6 } from "@nrk/reactive-element";
import SpringStreamsClass from "@nrk/player-tracker/vendor/springstreams.js";
var TrackingElement = class _TrackingElement extends ReactiveElement7 {
  static snowplowBundle = void 0;
  static InjectSnowplowBundle(bundle) {
    _TrackingElement.snowplowBundle = bundle;
  }
  static get reactiveAttributes() {
    return {
      ...super.reactiveAttributes,
      autoplay: Boolean,
      gaTrackingId: String,
      gaAppId: String,
      snowplowAppId: String,
      snowplowPlatform: String,
      snowplowUrl: String,
      snowplowServiceId: String
    };
  }
  static get reactiveProperties() {
    return {
      ...super.reactiveProperties,
      activeSession: Object,
      startTime: Number,
      startTimeLive: Object,
      epgState: ReactiveState6,
      user: Object
    };
  }
  /** The PlayerElement parent instance */
  player;
  playerTracker;
  trackingSession;
  sourceId;
  /** Shadowed player property/reactive attributes */
  activeSession;
  autoplay;
  startTimeOndemand;
  startTimeLive;
  epgState;
  user;
  gaTrackingId = "";
  gaAppId = "";
  snowplowAppId = "";
  snowplowUrl = "";
  snowplowPlatform = "";
  snowplowServiceId = "";
  _logger = getLogger("TrackingElement");
  _unshadowProperties;
  readyCallback() {
    super.readyCallback();
    const gaAppId = this.gaAppId;
    const snowplowAppId = this.snowplowAppId;
    const snowplowUrl = this.snowplowUrl;
    const snowplowPlatform = this.snowplowPlatform;
    const snowplowServiceId = this.snowplowServiceId;
    const gaTrackingId = this.gaTrackingId;
    if (!gaTrackingId) {
      throw new Error("Missing attribute 'gatrackingid' which is required for tracking");
    }
    this.sourceId = this.getAttribute("source-id") || void 0;
    const aiDiagnosticsId = this.getAttribute("aidiagnosticsid") || void 0;
    const player = getElementOfType(this);
    this.player = player;
    this.playerTracker = new PlayerTracker(
      {
        name: this.player.playerName,
        version: this.player.playerVersionNumber,
        build: this.player.playerVersion() || "unknown"
      },
      {
        gaTrackingId,
        gaAppId,
        aiDiagnosticsId,
        snowplowAppId,
        snowplowUrl,
        snowplowPlatform,
        snowplowServiceId,
        scoresLibrary: SpringStreamsClass
      },
      _TrackingElement.snowplowBundle
    );
    player.addEventListener(PlayerEvents.SessionCreated, this);
    player.addEventListener(PlayerEvents.PlayNextEpisode, this);
    player.addEventListener(PlayerEvents.TrackQoS, this);
    this._unshadowProperties = this.player.shadowProperties(
      ["activeSession", "epgState", "user", "seekToStartPoint", "startTimeOndemand", "startTimeLive"],
      this
    );
  }
  disconnectedCallback() {
    this.player?.removeEventListener(PlayerEvents.SessionCreated, this);
    this.player?.removeEventListener(PlayerEvents.PlayNextEpisode, this);
    this.player?.removeEventListener(PlayerEvents.TrackQoS, this);
    this.playerTracker = void 0;
    this.player = void 0;
    this.trackingSession = void 0;
    this._unshadowProperties?.();
    super.disconnectedCallback();
  }
  updating(propName, oldValue) {
    super.updating(propName, oldValue);
    if (propName === "epgState" && oldValue?.events !== void 0) {
      oldValue.events.off("programchange", this.trackProgramChange, this);
    }
  }
  updated(changedProperties) {
    super.updated(changedProperties);
    if (changedProperties.has("activeSession")) {
      if (this.trackingSession !== void 0) {
        this.trackingSession = void 0;
      }
      if (this.activeSession !== void 0) {
        this.trackingSession = this.createTrackingSession(this.activeSession);
        if (this.trackingSession && this.user?.id) {
          this.trackingSession.snowplowTracker?.setUser(this.user.id);
        }
      }
    }
    if (changedProperties.has("epgState") && this.epgState?.events !== void 0) {
      this.epgState.events.on("programchange", this.trackProgramChange, this);
    }
    if (changedProperties.has("autoplay")) {
      this.trackingSession?.gaSender?.updateFields({ autoplay: this.autoplay });
      this.trackingSession?.snowplowTracker?.setMediaAutoplay(this.autoplay ?? false);
    }
    if (changedProperties.has("user")) {
      this.trackingSession?.gaSender?.updateFields({
        userId: this.user?.id,
        userSegment: this.user?.userSegments,
        activeExperimentId: this.user?.activeExperimentId,
        activeExperimentVariant: this.user?.activeExperimentVariant?.toString()
      });
      if (this.user?.id) {
        this.trackingSession?.snowplowTracker?.setUser(this.user.id);
      }
    }
  }
  handleEvent(event) {
    if (event.type === PlayerEvents.PlayNextEpisode) {
      this.trackingSession?.progressTracker?.finalizeProgressReporting(this.activeSession?.getMediaRange().end);
    }
    if (event.type === PlayerEvents.TrackQoS) {
      const castedEvent = event;
      let urlParts = { mediaUrlHost: "", mediaUrlPath: "", mediaUrlQuery: "" };
      if (castedEvent.detail.mediaUrl) {
        const url = new URL(castedEvent.detail.mediaUrl);
        urlParts = { mediaUrlHost: url.origin, mediaUrlPath: url.pathname, mediaUrlQuery: url.search };
      }
      const properties = { ...castedEvent.detail, ...urlParts };
      this.playerTracker?.appTracker.trackQoSEvent({ properties });
    }
  }
  createTrackingSession(playbackSession) {
    const { epgState, player, playerTracker } = this;
    const { psapiClient, metadata, manifest } = this.player ?? {};
    const isTrackedRemotely = playbackSession.getAdapterInfo().chromecast === true;
    if (!manifest || !metadata || !player || !playerTracker || !psapiClient) {
      const props = { manifest, metadata, player, playerTracker, psapiClient };
      const missing = Object.keys(props).filter((key) => props[key] === void 0);
      this._logger.log(`Tracking needs: ${missing.join(", ")}`);
      return;
    }
    if (!("playable" in manifest)) {
      this._logger.log("Tracking only applies to playable media.");
      return;
    }
    const epgEvents = epgState?.events;
    if (!isTrackedRemotely && metadata.isChannel && !epgEvents) {
      this._logger.log("Tracking needs epgState.events (media is channel)");
      return;
    }
    const mediaInfo = playbackSession.getMediaInfo();
    this._logger.log("Creating tracking session for playback session:", player.src, mediaInfo);
    const sessionConfig = this.createSessionConfig(player, metadata, manifest, isTrackedRemotely);
    return playerTracker.trackSession(playbackSession, psapiClient, sessionConfig, epgEvents);
  }
  createSessionConfig(player, metadata, manifest, isTrackedRemotely) {
    const sessionConfig = {
      metadata: {
        mediaId: metadata.id ?? "unknown",
        mediaTitle: metadata.heading ?? "unknown",
        cdnName: manifest.statistics.qualityOfExperience.cdnName,
        streamingConfigName: manifest.statistics.qualityOfExperience.clientName
      }
    };
    if (isTrackedRemotely) {
      return sessionConfig;
    }
    sessionConfig.ga = {
      accumulatedTimeEvents: [120],
      fields: {
        sourceId: this.sourceId,
        autoplay: this.autoplay,
        userId: this.user?.id,
        userSegment: this.user?.userSegments,
        activeExperimentId: this.user?.activeExperimentId,
        activeExperimentVariant: this.user?.activeExperimentVariant?.toString(),
        fields: {
          eventLabel: metadata.heading,
          ...manifest.statistics.ga
        }
      }
    };
    sessionConfig.snowplow = {
      serviceId: player.snowplowServiceId,
      appId: this.snowplowAppId,
      platform: this.snowplowPlatform,
      mediaId: metadata.isChannel ? "" : metadata.id,
      contentKind: "",
      contentSource: manifest.statistics.snowplow.source,
      channelId: metadata.isChannel ? metadata.id : "",
      autoplay: this.autoplay ?? false,
      startTime: this.startTimeOndemand ?? 0,
      startTimeLive: this.startTimeLive,
      endpointURL: this.snowplowUrl
    };
    sessionConfig.ecommerce = {
      tracker: {
        sendCompleteEvent: () => this.trackEcommerceEvent(PlayerEvents.EcommerceConsumeComplete),
        sendStartEvent: () => this.trackEcommerceEvent(PlayerEvents.EcommerceConsumeStart)
      }
    };
    sessionConfig.scores = {
      config: manifest.statistics.scores
    };
    if (metadata.id) {
      sessionConfig.progress = {
        trackingCallback: this.trackProgress.bind(this),
        programId: metadata.id,
        playbackStartTime: player.startTimeOndemand ?? 0
      };
    }
    return sessionConfig;
  }
  trackEcommerceEvent(eventName) {
    this.dispatchEvent(new CustomEvent(eventName, { bubbles: true, detail: eventName }));
  }
  trackProgramChange(epgEntry) {
    this.dispatchEvent(new CustomEvent(PlayerEvents.EPGChange, { bubbles: true, detail: epgEntry }));
  }
  trackProgress(progress) {
    this.dispatchEvent(new CustomEvent(PlayerEvents.TrackProgress, { bubbles: true, detail: progress }));
  }
};

// src/volume/volume-element.js
import { ReactiveElement as ReactiveElement8 } from "@nrk/reactive-element";
import { FocusWithinController as FocusWithinController2 } from "@nrk/element-focus-within-controller";
var VolumeElement = class extends ReactiveElement8 {
  static get reactiveProperties() {
    return {
      ...super.reactiveProperties,
      muted: Boolean,
      volume: Number,
      //used by FocusWithinController
      focusWithin: Boolean
    };
  }
  /** Flag indicating that audio is muted  (shadowed Player property) */
  muted = false;
  /** The audio volume level from 0 to 1  (shadowed Player property) */
  volume = 1;
  /** used by FocusWithinController */
  focusWithin = false;
  focusWithinController;
  /** The PlayerElement parent instance */
  player;
  _unshadowProperties;
  constructor() {
    super();
    this.focusWithinController = new FocusWithinController2(this, {
      onChanged: () => {
        this.focusWithin = this.focusWithinController.value;
        this.classList.toggle("focus-within", this.focusWithin);
      }
    });
  }
  readyCallback() {
    super.readyCallback();
    this.player = getElementOfType(this);
    this._unshadowProperties = this.player.shadowProperties(["muted", "volume"], this);
    this.addEventListener("wheel", this, platform_default.supportsPassiveEvents ? { passive: false } : void 0);
    this.muted = this.player.muted;
    this.volume = this.player.volume;
  }
  disconnectedCallback() {
    this.removeEventListener("wheel", this);
    this._unshadowProperties?.();
    this.player = null;
    super.disconnectedCallback();
  }
  handleEvent(event) {
    if (event.type === "wheel" && event.target?.nodeName === "INPUT") {
      event.preventDefault();
      event.stopPropagation();
      const target = event.target;
      const mouseWheelEvent = event;
      if (mouseWheelEvent.deltaY == 0) {
        return;
      }
      const currentVolume = this.muted ? 0 : this.volume;
      const change = mouseWheelEvent.deltaY > 0 ? -0.1 : 0.1;
      const newVolume = Math.max(Math.min(currentVolume + change, 1), 0);
      if (newVolume === currentVolume) {
        return;
      }
      target.value = String(newVolume * 100);
      target.dispatchEvent(new Event("change", { cancelable: true, bubbles: true }));
    }
  }
};

// src/player/seek-images-state.js
import { WebVTTDataFile } from "@nrk/player-core/utils.js";
import { ReactiveState as ReactiveState7 } from "@nrk/reactive-element/reactive-state.js";

// src/utils/limiter.js
var Limiter = class {
  concurrency;
  pending;
  jobs;
  onDoneCbs;
  timeout;
  _done;
  _run;
  constructor(options) {
    this.concurrency = options.concurrency || 100;
    this.pending = 0;
    this.jobs = [];
    this.onDoneCbs = [];
    this._done = this.done.bind(this);
    this._run = this.run.bind(this);
  }
  get length() {
    return this.pending + this.jobs.length;
  }
  // Called upon completion of a job. Calls run() again
  // to pluck the next job off the queue, if it exists.
  done() {
    this.pending--;
    this._run();
  }
  run() {
    while (this.pending < this.concurrency && this.jobs.length) {
      this.pending++;
      const job = this.jobs.shift();
      job(this._done);
    }
    while (this.length === 0 && this.onDoneCbs.length) {
      const callback = this.onDoneCbs.pop();
      callback();
    }
    if (this.timeout) {
      window.clearTimeout(this.timeout);
    }
  }
  onDone(func) {
    this.onDoneCbs.push(func);
  }
  push(job) {
    this.jobs.push(job);
    this.timeout = window.setTimeout(this._run, 5);
  }
};

// src/player/seek-images-state.js
var LIVE_REALOAD_INTERVAL = 1e4;
var QUEUE_RUN_INTERVAL = 400;
var IDLE_CALLBACK_TIMEOUT = 500;
var SeekImagesState = class extends ReactiveState7 {
  currentImages = [];
  url;
  variant = "multiple";
  _imageList = [];
  _cues;
  _liveReloadIntervalId;
  _options;
  _taskList = [];
  _taskHandle = null;
  _preloaded = false;
  _logger = getLogger("SeekImagesState");
  _limiter = new Limiter({ concurrency: 2 });
  constructor(thumbnails, options) {
    super();
    this._options = options ? options : { imageWidth: 320 };
    this._loadSeekingImages = this._loadSeekingImages.bind(this);
    this.runTaskQueue = this.runTaskQueue.bind(this);
    if (this.isThumbnail2D(thumbnails)) {
      this.url = thumbnails.url;
      this.variant = "sprite";
    } else {
      if (thumbnails.length > 1) {
        const foundSize = thumbnails.find((thumb) => thumb.width === this._options.imageWidth);
        this.url = foundSize ? foundSize.url : void 0;
      } else {
        this.url = thumbnails[0] ? thumbnails[0].url : void 0;
      }
    }
    this._loadSeekingImages().catch((error) => {
      this._logger.error("Error when loading scrubber images", error);
    });
    if (this._options.isChannelStream && this._options.channelImagesEnabled) {
      this._liveReloadIntervalId = window.setInterval(this._loadSeekingImages, LIVE_REALOAD_INTERVAL);
    }
    this._limiter.onDone(() => {
      this._logger.info("Scrubber thumbnails preload is complete.");
    });
  }
  destroy() {
    super.destroy();
    window.clearInterval(this._liveReloadIntervalId);
    window.clearTimeout(this._taskHandle);
  }
  hasCurrentImages() {
    return this.currentImages.length > 0;
  }
  updateCurrentImages(currentTime, imageInterval = 15, contextImages = 3) {
    if (!this._cues) {
      return;
    }
    const foundCues = this._cues.searchCues(currentTime, imageInterval, contextImages);
    this.currentImages = foundCues.map((cue) => this.toImageData(cue));
  }
  imageAt(time) {
    if (this._preloaded) {
      if (this._imageList[time] && this.variant === "multiple") {
        return this._imageList[time];
      }
      const cue = this._cues?.search(time);
      return this.toImageData(cue);
    } else {
      const cue = this._cues?.search(time);
      return this.toImageData(cue);
    }
  }
  preload() {
    this._logger.info("Starting scrubber thumbnails preload");
    this.fetchAllImages();
  }
  async _loadSeekingImages() {
    if (this.url && !this._options.isChannelStream || this.url && this._options.isChannelStream && this._options.channelImagesEnabled) {
      this._logger.info("loading scrubber thumbnails from", this.url);
      this._cues = await WebVTTDataFile.load(this.url, (cue) => {
        if (cue.text.includes("#xywh")) {
          const [url, coord] = cue.text.split("#");
          const [x, y, width, height] = coord.split("=")[1].split(",").map((c) => parseInt(c, 10));
          return { url: new URL(url, this.url).href, x, y, width, height };
        }
        return { url: new URL(cue.text, this.url).href, x: 0, y: 0, width: 0, height: 0 };
      });
    }
  }
  async fetchAllImages() {
    const seenUrls = [];
    if (this._cues) {
      for (let i = 0; i < this._cues.cues.length; i++) {
        const cue = this._cues.cues[i];
        if (seenUrls.includes(cue.data.url)) {
          continue;
        }
        if (this.variant === "sprite") {
          const img = new Image();
          img.src = cue.data.url;
        } else {
          this._taskList.push({ url: cue.data.url, startTime: cue.startTime });
        }
        seenUrls.push(cue.data.url);
      }
      if (!this._taskHandle) {
        this._taskHandle = window.hasOwnProperty("requestIdleCallback") ? requestIdleCallback(this.runTaskQueue, { timeout: IDLE_CALLBACK_TIMEOUT }) : setTimeout(this.runTaskQueue, QUEUE_RUN_INTERVAL);
      }
    }
  }
  async runTaskQueue(deadline) {
    if (!deadline) {
      while (this._taskList.length) {
        this.schedule(this._taskList.shift());
      }
    } else {
      while ((deadline.timeRemaining() > 0 || deadline.didTimeout) && this._taskList.length) {
        this.schedule(this._taskList.shift());
      }
    }
    if (this._taskList.length) {
      this._taskHandle = window.hasOwnProperty("requestIdleCallback") ? requestIdleCallback(this.runTaskQueue, { timeout: IDLE_CALLBACK_TIMEOUT }) : setTimeout(this.runTaskQueue, QUEUE_RUN_INTERVAL);
    } else {
      clearTimeout(this._taskHandle);
      this._taskHandle = 0;
      this._preloaded = true;
    }
  }
  schedule(task) {
    this._limiter.push(
      (cb) => fetch(task.url).then((res) => res.blob()).then(async (blob) => {
        const objectUrl = URL.createObjectURL(blob);
        this._imageList[task.startTime] = this.toImageData(objectUrl);
        cb();
      })
    );
  }
  toImageData(cue) {
    if (!cue) {
      return { url: "", x: 0, y: 0, width: 0, height: 0 };
    }
    if (typeof cue === "string") {
      return { url: cue, x: 0, y: 0, width: 0, height: 0 };
    }
    if ("x" in cue.data) {
      return { url: cue.data.url, x: cue.data.x, y: cue.data.y, width: cue.data.width, height: cue.data.height };
    }
    return { url: "", x: 0, y: 0, width: 0, height: 0 };
  }
  isThumbnail2D(thumbnails) {
    return !Array.isArray(thumbnails);
  }
};
export {
  ControlsElement,
  ElementPanic,
  ElementPanicMixin,
  KeyboardElement,
  PLAYER_ELEMENTS_VERSION,
  PlayerActions,
  PlayerElement,
  PlayerErrorTypes,
  PlayerPanicHandlerMixin,
  PosterElement,
  ScrubberElement,
  SeekImagesState,
  SubtitleState,
  SubtitlesElement,
  TrackingElement,
  VolumeElement,
  dateToReadableDateString,
  dateToReadableShortString,
  dateToReadableString,
  dateToReadableTimeString,
  durationToReadableString,
  durationToSeconds,
  formatDuration,
  getActionElement,
  getActionFromEvent,
  getElementOfType,
  getFontSize,
  getKeyActionsFromKeyName,
  getPositionFromEvent,
  parseDuration,
  safeCEDefine
};
